import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, postForm, uploadFile } from './client'
import type {
  ActivityEntry,
  AppNotification,
  Comment,
  Dashboard,
  DashboardView,
  Lookup,
  MyWork,
  Paged,
  Project,
  ProjectDetail,
  Screen,
  ScreenCreated,
  SearchResults,
  Task,
  Team,
  User
} from './types'

/**
 * React Query-nøgler samlet ét sted, så realtidsopdateringer kan invalidere
 * præcis de forespørgsler der er berørt.
 */
export const queryKeys = {
  myWork: ['me', 'work'] as const,
  notifications: ['me', 'notifications'] as const,
  projects: (filters?: Record<string, unknown>) => ['projects', filters ?? {}] as const,
  project: (id: number) => ['projects', id] as const,
  tasks: (filters?: Record<string, unknown>) => ['tasks', filters ?? {}] as const,
  projectStatuses: ['lookups', 'project-statuses'] as const,
  taskStatuses: ['lookups', 'task-statuses'] as const,
  priorities: ['lookups', 'priorities'] as const,
  users: ['users'] as const,
  teams: ['teams'] as const,
  screens: ['screens'] as const,
  dashboards: ['dashboards'] as const,
  dashboardView: (id: number) => ['dashboards', id, 'view'] as const,
  activity: (filters?: Record<string, unknown>) => ['activity', filters ?? {}] as const,
  search: (term: string) => ['search', term] as const
}

function toQueryString(filters: Record<string, unknown>) {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value))
    }
  }
  const query = params.toString()
  return query ? `?${query}` : ''
}

// ---------------------------------------------------------------------------
// Forespørgsler
// ---------------------------------------------------------------------------

export const useMyWork = () =>
  useQuery({ queryKey: queryKeys.myWork, queryFn: () => api.get<MyWork>('/api/me/work') })

export const useNotifications = () =>
  useQuery({
    queryKey: queryKeys.notifications,
    queryFn: () => api.get<AppNotification[]>('/api/me/notifications')
  })

export const useProjects = (filters: Record<string, unknown> = {}) =>
  useQuery({
    queryKey: queryKeys.projects(filters),
    queryFn: () => api.get<Paged<Project>>(`/api/projects${toQueryString(filters)}`)
  })

export const useProject = (id: number) =>
  useQuery({
    queryKey: queryKeys.project(id),
    queryFn: () => api.get<ProjectDetail>(`/api/projects/${id}`),
    enabled: Number.isFinite(id) && id > 0
  })

export const useTasks = (filters: Record<string, unknown> = {}) =>
  useQuery({
    queryKey: queryKeys.tasks(filters),
    queryFn: () => api.get<Paged<Task>>(`/api/tasks${toQueryString(filters)}`)
  })

/** Opslagsdata ændres sjældent, så de holdes længe i cachen. */
const lookupOptions = { staleTime: 10 * 60 * 1000 }

export const useProjectStatuses = () =>
  useQuery({ queryKey: queryKeys.projectStatuses, queryFn: () => api.get<Lookup[]>('/api/project-statuses'), ...lookupOptions })

export const useTaskStatuses = () =>
  useQuery({ queryKey: queryKeys.taskStatuses, queryFn: () => api.get<Lookup[]>('/api/task-statuses'), ...lookupOptions })

export const usePriorities = () =>
  useQuery({ queryKey: queryKeys.priorities, queryFn: () => api.get<Lookup[]>('/api/priorities'), ...lookupOptions })

/**
 * Brugerlisten. Skjulte brugere (postkasser, mødelokaler, servicekonti) er ikke
 * med som standard - se beslutning 036. Adminpanelet kan bede om dem med
 * `includeHidden` for at kunne sætte dem ind igen.
 */
export const useUsers = (options?: { includeHidden?: boolean }) => {
  const includeHidden = options?.includeHidden ?? false

  return useQuery({
    queryKey: [...queryKeys.users, { includeHidden }],
    queryFn: () => api.get<User[]>(`/api/users${includeHidden ? '?includeHidden=true' : ''}`),
    staleTime: 5 * 60 * 1000
  })
}

/** Kom i gang: hvem mangler at komme ind i systemet (beslutning 080). Kun Administrator. */
export const useOnboarding = (enabled = true) =>
  useQuery({
    queryKey: ['users', 'onboarding'],
    queryFn: () => api.get<import('./types').Onboarding>('/api/users/onboarding'),
    enabled
  })

/** Sender invitationen som SMS til de valgte. */
export function useSendInvitations() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (userIds: number[]) =>
      api.post<import('./types').InviteResult>('/api/users/onboarding/invite', { userIds }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['users'] })
      void client.invalidateQueries({ queryKey: ['activity'] })
    }
  })
}

export const useTeams = () =>
  useQuery({ queryKey: queryKeys.teams, queryFn: () => api.get<Team[]>('/api/teams'), staleTime: 5 * 60 * 1000 })

/** Medlemmerne i ét team. Hentes foerst naar teamet aabnes - listen er som regel kort. */
export const useTeamMembers = (teamId: number | null) =>
  useQuery({
    queryKey: ['teams', teamId, 'members'],
    queryFn: () => api.get<import('./types').UserRef[]>(`/api/teams/${teamId}/members`),
    enabled: Boolean(teamId)
  })

export const useSaveTeam = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ id, input }: { id?: number; input: import('./types').TeamInput }) =>
      id
        ? api.put<Team>(`/api/teams/${id}`, input)
        : api.post<Team>('/api/teams', input),
    onSuccess: () => client.invalidateQueries({ queryKey: ['teams'] })
  })
}

export const useDeleteTeam = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/teams/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['teams'] })
      // Sagerne mister deres team, saa listerne skal hentes igen.
      void client.invalidateQueries({ queryKey: ['projects'] })
    }
  })
}

export const useScreens = () =>
  useQuery({ queryKey: queryKeys.screens, queryFn: () => api.get<Screen[]>('/api/screens') })

/** Den TV-klient, serveren har klar, og hvor mange skaerme der er bagud (beslutning 074). */
export const useTvClientPackage = () =>
  useQuery({
    queryKey: [...queryKeys.screens, 'client'],
    queryFn: () => api.get<import('./types').TvClientPackage>('/api/screens/client')
  })

/** Raspberry Pi-imaget til nye skaerme (beslutning 075). Spoerger igen, mens der bygges. */
export const usePiImage = () =>
  useQuery({
    queryKey: [...queryKeys.screens, 'image'],
    queryFn: () => api.get<import('./types').PiImage>('/api/screens/image'),
    refetchInterval: (query) => (query.state.data?.buildState === 'building' ? 30_000 : false)
  })

/** Et link til at hente imaget, der virker i 10 minutter. */
export const useCreatePiImageLink = () =>
  useMutation({
    mutationFn: () => api.post<import('./types').PiImageLink>('/api/screens/image/link', {})
  })

/** Tilknytter skaermen med koden, der staar paa TV'et (beslutning 075). */
export const useClaimScreenPairing = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: import('./types').ScreenPairingClaimInput) =>
      api.post<Screen>('/api/screens/pairing/claim', input),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.screens })
  })
}

/** Sender opdateringen til hver aktiv skaerm, der er bagud. Sker aldrig af sig selv. */
export const useUpdateAllScreens = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: () => api.post<import('./types').ScreenUpdateAllResult>('/api/screens/update-all', {}),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.screens })
  })
}

export const useDashboards = () =>
  useQuery({ queryKey: queryKeys.dashboards, queryFn: () => api.get<Dashboard[]>('/api/dashboards') })

export const useDashboardView = (id: number) =>
  useQuery({
    queryKey: queryKeys.dashboardView(id),
    queryFn: () => api.get<DashboardView>(`/api/dashboards/${id}/view`),
    enabled: id > 0
  })

export const useActivity = (filters: Record<string, unknown> = {}) =>
  useQuery({
    queryKey: queryKeys.activity(filters),
    queryFn: () => api.get<Paged<ActivityEntry>>(`/api/activity${toQueryString(filters)}`)
  })

export const useSearch = (term: string) =>
  useQuery({
    queryKey: queryKeys.search(term),
    queryFn: () => api.get<SearchResults>(`/api/search?q=${encodeURIComponent(term)}`),
    enabled: term.trim().length >= 2
  })

// ---------------------------------------------------------------------------
// Ændringer
// ---------------------------------------------------------------------------

/** Efter en ændring hentes de berørte data igen - serveren er altid facit. */
function useInvalidateWork() {
  const client = useQueryClient()
  return () => {
    void client.invalidateQueries({ queryKey: ['projects'] })
    void client.invalidateQueries({ queryKey: ['tasks'] })
    void client.invalidateQueries({ queryKey: ['me'] })
    void client.invalidateQueries({ queryKey: ['activity'] })
    void client.invalidateQueries({ queryKey: ['dashboards'] })
  }
}

export interface TaskStatusChange {
  taskId: number
  statusId: number
  waitingReason?: string
}

export function useChangeTaskStatus() {
  const invalidate = useInvalidateWork()
  return useMutation({
    mutationFn: ({ taskId, statusId, waitingReason }: TaskStatusChange) =>
      api.post<Task>(`/api/tasks/${taskId}/status`, { statusId, waitingReason }),
    onSuccess: invalidate
  })
}

export function useCreateProject() {
  const invalidate = useInvalidateWork()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<ProjectDetail>('/api/projects', body),
    onSuccess: invalidate
  })
}

export function useUpdateProject(id: number) {
  const invalidate = useInvalidateWork()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.put<ProjectDetail>(`/api/projects/${id}`, body),
    onSuccess: invalidate
  })
}

/** Hvilke af kundens enheder servicesagen handler om. Listen erstatter den gamle (beslutning 076). */
export function useSetServiceDevices(projectId: number) {
  const invalidate = useInvalidateWork()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (deviceIds: number[]) =>
      api.put<ProjectDetail>(`/api/projects/${projectId}/service-devices`, { deviceIds }),
    onSuccess: () => {
      invalidate()
      void client.invalidateQueries({ queryKey: ['devices'] })
    }
  })
}

/** Sletter en sag permanent. Kun Superadmin - tænkt til testsager (beslutning 076). */
export function useDeleteProject() {
  const invalidate = useInvalidateWork()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/projects/${id}`),
    onSuccess: invalidate
  })
}

export function useCreateTask() {
  const invalidate = useInvalidateWork()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<Task>('/api/tasks', body),
    onSuccess: invalidate
  })
}

// ---------- Fremdrift, udført arbejde og mangler på en opgave (beslutning 085) ----------

/** Sætter opgavens fremdrift, 0-100. */
export function useSetTaskProgress(taskId: number) {
  const invalidate = useInvalidateWork()
  return useMutation({
    mutationFn: (progressPercent: number) => api.post<Task>(`/api/tasks/${taskId}/progress`, { progressPercent }),
    onSuccess: invalidate
  })
}

export const useTaskWorkLog = (taskId: number, enabled = true) =>
  useQuery({
    queryKey: ['tasks', taskId, 'work-log'],
    queryFn: () => api.get<import('./types').TaskWorkLog[]>(`/api/tasks/${taskId}/work-log`),
    enabled
  })

/** Skriver udført arbejde - og kan samtidig sætte fremdriften. */
export function useAddTaskWorkLog(taskId: number) {
  const invalidate = useInvalidateWork()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: { body: string; progressPercent?: number | null }) =>
      api.post<import('./types').TaskWorkLog>(`/api/tasks/${taskId}/work-log`, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['tasks', taskId, 'work-log'] })
      invalidate()
    }
  })
}

export const useTaskMissingItems = (taskId: number, enabled = true) =>
  useQuery({
    queryKey: ['tasks', taskId, 'missing-items'],
    queryFn: () => api.get<import('./types').TaskMissingItem[]>(`/api/tasks/${taskId}/missing-items`),
    enabled
  })

/** Tilføjer, retter eller fjerner et punkt på mangellisten. Svaret er hele listen. */
export function useChangeTaskMissingItems(taskId: number) {
  const invalidate = useInvalidateWork()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (
      change:
        | { kind: 'add'; text: string }
        | { kind: 'update'; itemId: number; body: Record<string, unknown> }
        | { kind: 'delete'; itemId: number }
    ) => {
      const base = `/api/tasks/${taskId}/missing-items`
      if (change.kind === 'add') return api.post<import('./types').TaskMissingItem[]>(base, { text: change.text })
      if (change.kind === 'update')
        return api.put<import('./types').TaskMissingItem[]>(`${base}/${change.itemId}`, change.body)
      return api.delete<import('./types').TaskMissingItem[]>(`${base}/${change.itemId}`)
    },
    onSuccess: (items) => {
      client.setQueryData(['tasks', taskId, 'missing-items'], items)
      invalidate()
    }
  })
}

/** "Til næste gang" øverst på sagen (beslutning 143). */
export const useProjectNeeds = (projectId: number) =>
  useQuery({
    queryKey: ['projects', projectId, 'needs'],
    queryFn: () => api.get<import('./types').ProjectNeed[]>(`/api/projects/${projectId}/needs`),
    enabled: Number.isFinite(projectId) && projectId > 0
  })

/** Skriver, sætter et trin på eller fjerner en note. Svaret er hele listen. */
export function useChangeProjectNeeds(projectId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (
      change:
        | { kind: 'add'; text: string; needsOrder: boolean; needsPacking: boolean }
        | { kind: 'step'; needId: number; step: import('./types').ProjectNeedStep; value: boolean }
        | { kind: 'delete'; needId: number }
    ) => {
      const base = `/api/projects/${projectId}/needs`
      if (change.kind === 'add')
        return api.post<import('./types').ProjectNeed[]>(base, {
          text: change.text,
          needsOrder: change.needsOrder,
          needsPacking: change.needsPacking
        })
      if (change.kind === 'step')
        return api.post<import('./types').ProjectNeed[]>(`${base}/${change.needId}/step`, {
          step: change.step,
          value: change.value
        })
      return api.delete<import('./types').ProjectNeed[]>(`${base}/${change.needId}`)
    },
    onSuccess: (needs) => {
      client.setQueryData(['projects', projectId, 'needs'], needs)
      void client.invalidateQueries({ queryKey: ['activity'] })
    }
  })
}

/**
 * Sager klar til faktura (beslutning 147) - kun kontoret. Under nøglen ['projects', ...], så
 * realtidsbeskeden, der allerede henter sagerne igen, også opdaterer listen og de røde tal.
 */
export const useInvoiceReady = (enabled = true) =>
  useQuery({
    queryKey: ['projects', 'invoicing'],
    queryFn: () => api.get<import('./types').InvoiceReadyProject[]>('/api/invoicing'),
    enabled
  })

export const useInvoiceReadyCount = (enabled = true) =>
  useQuery({
    queryKey: ['projects', 'invoicing', 'count'],
    queryFn: () => api.get<{ count: number }>('/api/invoicing/count'),
    enabled,
    refetchInterval: 2 * 60 * 1000
  })

/** "Faktureret": sagen sættes til "Færdig". */
export function useMarkInvoiced() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (projectId: number) => api.post<void>(`/api/invoicing/${projectId}/invoiced`, {}),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['projects'] })
      void client.invalidateQueries({ queryKey: ['activity'] })
    }
  })
}

/** Sagens kasser og hvor de er (beslutning 144 og 145). Kasse 1 er altid med. */
export const useProjectBoxes = (projectId: number) =>
  useQuery({
    queryKey: ['projects', projectId, 'boxes'],
    queryFn: () => api.get<import('./types').ProjectBox[]>(`/api/projects/${projectId}/boxes`),
    enabled: Number.isFinite(projectId) && projectId > 0
  })

/** Tilføjer, navngiver, fjerner eller flytter en kasse. Svaret er alle sagens kasser. */
export function useChangeProjectBoxes(projectId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (
      change:
        | { kind: 'add'; name?: string }
        | { kind: 'rename'; number: number; name: string }
        | { kind: 'remove'; number: number }
        | { kind: 'move'; number: number; location: import('./types').CaseBoxLocation }
    ) => {
      const base = `/api/projects/${projectId}/boxes`
      if (change.kind === 'add') return api.post<import('./types').ProjectBox[]>(base, { name: change.name })
      if (change.kind === 'rename')
        return api.put<import('./types').ProjectBox[]>(`${base}/${change.number}`, { name: change.name })
      if (change.kind === 'remove') return api.delete<import('./types').ProjectBox[]>(`${base}/${change.number}`)
      return api.post<import('./types').ProjectBox[]>(`${base}/${change.number}/move`, { location: change.location })
    },
    onSuccess: (boxes) => {
      client.setQueryData(['projects', projectId, 'boxes'], boxes)
      void client.invalidateQueries({ queryKey: ['activity'] })
    }
  })
}

export function useUpdateTask(id: number) {
  const invalidate = useInvalidateWork()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.put<Task>(`/api/tasks/${id}`, body),
    onSuccess: invalidate
  })
}

/** "Tag opgaven" - en på teamet bliver hovedansvarlig (beslutning 142). */
export function useTakeTask() {
  const invalidate = useInvalidateWork()
  return useMutation({
    mutationFn: (taskId: number) => api.post<Task>(`/api/tasks/${taskId}/take`),
    onSuccess: invalidate
  })
}

export function useAddProjectComment(projectId: number) {
  const invalidate = useInvalidateWork()
  return useMutation({
    mutationFn: (body: string) => api.post<Comment>(`/api/projects/${projectId}/comments`, { body }),
    onSuccess: invalidate
  })
}

export function useAddTaskComment(taskId: number) {
  const invalidate = useInvalidateWork()
  return useMutation({
    mutationFn: (body: string) => api.post<Comment>(`/api/tasks/${taskId}/comments`, { body }),
    onSuccess: invalidate
  })
}

export function useMarkNotificationRead() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post<void>(`/api/me/notifications/${id}/read`),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.notifications })
  })
}

export function useCreateScreen() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<ScreenCreated>('/api/screens', body),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.screens })
  })
}

export function useSendScreenCommand() {
  return useMutation({
    mutationFn: ({ screenId, type, message }: { screenId: number; type: string; message?: string }) =>
      api.post<unknown>(`/api/screens/${screenId}/command`, { type, message })
  })
}

export function useUpdateScreen(id: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.put<Screen>(`/api/screens/${id}`, body),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.screens })
  })
}

export function useUpdateScreenSchedule(id: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (days: unknown[]) => api.put<unknown>(`/api/screens/${id}/schedule`, { days }),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.screens })
  })
}

export function useSetUserRoles(userId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (roleNames: string[]) => api.put<User>(`/api/users/${userId}/roles`, { roleNames }),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.users })
  })
}

// ---------------------------------------------------------------------------
// Kalender / planlægning
// ---------------------------------------------------------------------------

export const useDaySchedule = (date: string) =>
  useQuery({
    queryKey: ['schedule', 'day', date],
    queryFn: () => api.get<import('./types').DaySchedule>(`/api/schedule/day?date=${date}`)
  })

/**
 * Min egen dag (beslutning 061) - det montoeren ser oeverst paa forsiden.
 *
 * Egen sti frem for et filter paa dagsoversigten: saa henter mobilen ikke
 * tretten maends planlaegning for at vise én.
 */
export const useMyDay = (date: string) =>
  useQuery({
    queryKey: ['schedule', 'my-day', date],
    queryFn: () => api.get<import('./types').ScheduleRow>(`/api/schedule/my-day?date=${date}`)
  })

export const useScheduleRange = (from: string, to: string) =>
  useQuery({
    queryKey: ['schedule', 'range', from, to],
    queryFn: () => api.get<import('./types').WorkAssignment[]>(`/api/schedule?from=${from}&to=${to}`)
  })

export function useCreateAssignment() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: import('./types').WorkAssignmentInput) =>
      api.post<import('./types').WorkAssignment[]>('/api/schedule', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['schedule'] })
      void client.invalidateQueries({ queryKey: ['activity'] })
    }
  })
}

export function useDeleteAssignment() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/schedule/${id}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['schedule'] })
  })
}

/**
 * Ændrer en planlagt post. Bruges både af redigeringsdialogen og af
 * træk-og-slip i ugevisningen, hvor kun medarbejder og dato skifter.
 */
export function useUpdateAssignment() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: import('./types').WorkAssignmentInput }) =>
      api.put<import('./types').WorkAssignment>(`/api/schedule/${id}`, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['schedule'] })
      void client.invalidateQueries({ queryKey: ['activity'] })
    }
  })
}

/** Kopierer en uges planlægning over på en anden uge. */
export function useCopyWeek() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: import('./types').CopyWeekInput) =>
      api.post<import('./types').CopyWeekResult>('/api/schedule/copy-week', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['schedule'] })
      void client.invalidateQueries({ queryKey: ['activity'] })
    }
  })
}

// ---------------------------------------------------------------------------
// Fravaer og ferie (beslutning 053)
// ---------------------------------------------------------------------------

/**
 * Fravaer der roerer ved perioden. En ferie der begynder foer og slutter efter
 * kommer med - ellers ville en lang periode forsvinde fra ugen midt i den.
 *
 * `enabled` lader kaldet vente, til datoerne er regnet ud.
 */
export const useAbsences = (from: string, to: string, userId?: number) =>
  useQuery({
    queryKey: ['absences', from, to, userId ?? null],
    queryFn: () => {
      const params = new URLSearchParams({ from, to })
      if (userId) params.set('userId', String(userId))
      return api.get<import('./types').Absence[]>(`/api/absences?${params}`)
    },
    enabled: Boolean(from && to)
  })

/** Fravaerstyperne med dansk tekst og farve. De skifter aldrig i drift. */
export const useAbsenceTypes = () =>
  useQuery({
    queryKey: ['absence-types'],
    queryFn: () => api.get<import('./types').AbsenceType[]>('/api/absences/types'),
    staleTime: 60 * 60 * 1000
  })

export function useSaveAbsence() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id?: number; body: import('./types').AbsenceInput }) =>
      id
        ? api.put<import('./types').Absence>(`/api/absences/${id}`, body)
        : api.post<import('./types').Absence>('/api/absences', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['absences'] })
      // Kalenderen viser fravaeret paa raekkerne, saa den skal hentes igen.
      void client.invalidateQueries({ queryKey: ['schedule'] })
      void client.invalidateQueries({ queryKey: ['activity'] })
    }
  })
}

export function useDeleteAbsence() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/absences/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['absences'] })
      void client.invalidateQueries({ queryKey: ['schedule'] })
      void client.invalidateQueries({ queryKey: ['activity'] })
    }
  })
}

// ---------------------------------------------------------------------------
// Certifikater og kurser (beslutning 054)
// ---------------------------------------------------------------------------

export const useCertificates = (options?: {
  search?: string
  userId?: number
  dueOnly?: boolean
  includeInactive?: boolean
}) => {
  const params = new URLSearchParams()
  if (options?.search) params.set('search', options.search)
  if (options?.userId) params.set('userId', String(options.userId))
  if (options?.dueOnly) params.set('dueOnly', 'true')
  if (options?.includeInactive) params.set('includeInactive', 'true')
  const query = params.toString()

  return useQuery({
    queryKey: ['certificates', query],
    queryFn: () =>
      api.get<import('./types').Certificate[]>(`/api/certificates${query ? `?${query}` : ''}`)
  })
}

/** Hvor mange beviser der er udloebet, og hvor mange der udloeber snart. */
export const useCertificateSummary = () =>
  useQuery({
    queryKey: ['certificates', 'summary'],
    queryFn: () => api.get<import('./types').CertificateSummary>('/api/certificates/summary')
  })

/** Forslag til navne, saa de samme beviser kommer til at hedde det samme. */
export const useCertificateSuggestions = () =>
  useQuery({
    queryKey: ['certificate-suggestions'],
    queryFn: () => api.get<string[]>('/api/certificates/suggestions'),
    staleTime: 60 * 60 * 1000
  })

export function useSaveCertificate() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id?: number; body: import('./types').CertificateInput }) =>
      id
        ? api.put<import('./types').Certificate>(`/api/certificates/${id}`, body)
        : api.post<import('./types').Certificate>('/api/certificates', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['certificates'] })
      void client.invalidateQueries({ queryKey: ['activity'] })
    }
  })
}

export function useDeleteCertificate() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/certificates/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['certificates'] })
      void client.invalidateQueries({ queryKey: ['activity'] })
    }
  })
}

// ---------------------------------------------------------------------------
// Noegler og adgangsbrikker (beslutning 055)
// ---------------------------------------------------------------------------

export const useAccessKeys = (options?: {
  search?: string
  customerId?: number
  userId?: number
  outOnly?: boolean
  includeInactive?: boolean
}) => {
  const params = new URLSearchParams()
  if (options?.search) params.set('search', options.search)
  if (options?.customerId) params.set('customerId', String(options.customerId))
  if (options?.userId) params.set('userId', String(options.userId))
  if (options?.outOnly) params.set('outOnly', 'true')
  if (options?.includeInactive) params.set('includeInactive', 'true')
  const query = params.toString()

  return useQuery({
    queryKey: ['access-keys', query],
    queryFn: () =>
      api.get<import('./types').AccessKey[]>(`/api/access-keys${query ? `?${query}` : ''}`)
  })
}

export const useAccessKeySummary = () =>
  useQuery({
    queryKey: ['access-keys', 'summary'],
    queryFn: () => api.get<import('./types').AccessKeySummary>('/api/access-keys/summary')
  })

export const useAccessKeyTypes = () =>
  useQuery({
    queryKey: ['access-key-types'],
    queryFn: () => api.get<import('./types').AccessKeyType[]>('/api/access-keys/types'),
    staleTime: 60 * 60 * 1000
  })

/** Hele historikken for én noegle - hvem har haft den hvornaar. */
export const useAccessKeyLoans = (id: number | null) =>
  useQuery({
    queryKey: ['access-keys', 'loans', id],
    queryFn: () => api.get<import('./types').AccessKeyLoan[]>(`/api/access-keys/${id}/loans`),
    enabled: id !== null
  })

export function useSaveAccessKey() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id?: number; body: import('./types').AccessKeyInput }) =>
      id
        ? api.put<import('./types').AccessKey>(`/api/access-keys/${id}`, body)
        : api.post<import('./types').AccessKey>('/api/access-keys', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['access-keys'] })
      void client.invalidateQueries({ queryKey: ['activity'] })
    }
  })
}

export function useDeleteAccessKey() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/access-keys/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['access-keys'] })
      void client.invalidateQueries({ queryKey: ['activity'] })
    }
  })
}

/** Udleverer noeglen til en medarbejder. */
export function useHandOutAccessKey() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, userId, note }: { id: number; userId: number; note?: string | null }) =>
      api.post<import('./types').AccessKey>(`/api/access-keys/${id}/hand-out`, { userId, note }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['access-keys'] })
      void client.invalidateQueries({ queryKey: ['activity'] })
    }
  })
}

/** Registrerer noeglen som afleveret. */
export function useReturnAccessKey() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, note }: { id: number; note?: string | null }) =>
      api.post<import('./types').AccessKey>(`/api/access-keys/${id}/return`, { note }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['access-keys'] })
      void client.invalidateQueries({ queryKey: ['activity'] })
    }
  })
}

/**
 * Hvad afleveringsmappen kommer til at indeholde (beslutning 056).
 *
 * Selve PDF-en hentes med `downloadFile` - den er ikke en forespoergsel, der
 * skal caches, og den kan tage et par sekunder, mens fotoene hentes.
 */
export const useHandoverPreview = (projectId: number) =>
  useQuery({
    queryKey: ['handover', projectId],
    queryFn: () =>
      api.get<import('./types').HandoverPreview>(`/api/projects/${projectId}/handover/preview`)
  })

// ---------------------------------------------------------------------------
// Materiel og vaerktoej (beslutning 057)
// ---------------------------------------------------------------------------

export const useEquipment = (options?: {
  search?: string
  userId?: number
  categoryId?: number
  status?: import('./types').EquipmentStatus
  outOnly?: boolean
  dueOnly?: boolean
  includeInactive?: boolean
}) => {
  const params = new URLSearchParams()
  if (options?.search) params.set('search', options.search)
  if (options?.userId) params.set('userId', String(options.userId))
  if (options?.categoryId) params.set('categoryId', String(options.categoryId))
  if (options?.status) params.set('status', options.status)
  if (options?.outOnly) params.set('outOnly', 'true')
  if (options?.dueOnly) params.set('dueOnly', 'true')
  if (options?.includeInactive) params.set('includeInactive', 'true')
  const query = params.toString()

  return useQuery({
    queryKey: ['equipment', query],
    queryFn: () => api.get<import('./types').Equipment[]>(`/api/equipment${query ? `?${query}` : ''}`)
  })
}

export const useEquipmentSummary = () =>
  useQuery({
    queryKey: ['equipment', 'summary'],
    queryFn: () => api.get<import('./types').EquipmentSummary>('/api/equipment/summary')
  })

export const useEquipmentSuggestions = () =>
  useQuery({
    queryKey: ['equipment-suggestions'],
    queryFn: () => api.get<string[]>('/api/equipment/suggestions'),
    staleTime: 60 * 60 * 1000
  })

export const useEquipmentLoans = (id: number | null) =>
  useQuery({
    queryKey: ['equipment', 'loans', id],
    queryFn: () => api.get<import('./types').EquipmentLoan[]>(`/api/equipment/${id}/loans`),
    enabled: id !== null
  })

export const useEquipmentInspections = (id: number | null) =>
  useQuery({
    queryKey: ['equipment', 'inspections', id],
    queryFn: () => api.get<import('./types').EquipmentInspection[]>(`/api/equipment/${id}/inspections`),
    enabled: id !== null
  })

function equipmentInvalidation(client: ReturnType<typeof useQueryClient>) {
  void client.invalidateQueries({ queryKey: ['equipment'] })
  void client.invalidateQueries({ queryKey: ['activity'] })
}

export function useSaveEquipment() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id?: number; body: import('./types').EquipmentInput }) =>
      id
        ? api.put<import('./types').Equipment>(`/api/equipment/${id}`, body)
        : api.post<import('./types').Equipment>('/api/equipment', body),
    onSuccess: () => equipmentInvalidation(client)
  })
}

export function useDeleteEquipment() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/equipment/${id}`),
    onSuccess: () => equipmentInvalidation(client)
  })
}

/**
 * Udleverer grejet. Uden `userId` tager man det selv - det er den vej, knappen
 * "Tag den" paa maerkatets side gaar (beslutning 096).
 */
export function useHandOutEquipment() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      userId,
      expectedDays,
      projectId,
      note
    }: {
      id: number
      userId?: number | null
      expectedDays?: number | null
      projectId?: number | null
      note?: string | null
    }) =>
      api.post<import('./types').Equipment>(`/api/equipment/${id}/hand-out`, {
        userId,
        expectedDays,
        projectId,
        note
      }),
    onSuccess: () => equipmentInvalidation(client)
  })
}

/** Et enkelt stykke grej - siden man lander paa fra QR-maerkatet. */
export const useEquipmentItem = (id: number | null) =>
  useQuery({
    queryKey: ['equipment', 'item', id],
    queryFn: () => api.get<import('./types').Equipment>(`/api/equipment/${id}`),
    enabled: id !== null && !Number.isNaN(id)
  })

/**
 * Slaar vaerktoejet op paa koden fra QR-maerkatet. Serveren retter koden til -
 * et scan giver hele URL'en fra maerkatet, ikke bare de otte tegn.
 */
export const useEquipmentByCode = (code: string | undefined) =>
  useQuery({
    queryKey: ['equipment', 'by-code', code],
    queryFn: () => api.get<import('./types').Equipment>(`/api/equipment/by-code/${code}`),
    enabled: Boolean(code),
    retry: false
  })

/** Kategorierne - hvad slags vaerktoej det er (beslutning 096). */
export const useEquipmentCategories = (includeInactive = false) =>
  useQuery({
    queryKey: ['equipment-categories', includeInactive],
    queryFn: () =>
      api.get<import('./types').EquipmentCategory[]>(
        `/api/equipment/categories${includeInactive ? '?includeInactive=true' : ''}`
      ),
    staleTime: 5 * 60 * 1000
  })

function categoryInvalidation(client: ReturnType<typeof useQueryClient>) {
  void client.invalidateQueries({ queryKey: ['equipment-categories'] })
  equipmentInvalidation(client)
}

export function useSaveEquipmentCategory() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id?: number; body: import('./types').EquipmentCategoryInput }) =>
      id
        ? api.put<import('./types').EquipmentCategory>(`/api/equipment/categories/${id}`, body)
        : api.post<import('./types').EquipmentCategory>('/api/equipment/categories', body),
    onSuccess: () => categoryInvalidation(client)
  })
}

export function useDeleteEquipmentCategory() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/equipment/categories/${id}`),
    onSuccess: () => categoryInvalidation(client)
  })
}

/** Skifter tilstand: i orden, til service eller defekt. */
export function useSetEquipmentStatus() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      status,
      note
    }: {
      id: number
      status: import('./types').EquipmentStatus
      note?: string | null
    }) => api.post<import('./types').Equipment>(`/api/equipment/${id}/status`, { status, note }),
    onSuccess: () => equipmentInvalidation(client)
  })
}

/** Reservationerne paa ét stykke grej. Det aabne foerst. */
export const useEquipmentReservations = (id: number | null, includeClosed = false) =>
  useQuery({
    queryKey: ['equipment', 'reservations', id, includeClosed],
    queryFn: () =>
      api.get<import('./types').EquipmentReservation[]>(
        `/api/equipment/${id}/reservations${includeClosed ? '?includeClosed=true' : ''}`
      ),
    enabled: id !== null && !Number.isNaN(id)
  })

export function useReserveEquipment() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      fromDate,
      days,
      projectId,
      note
    }: {
      id: number
      fromDate?: string | null
      days: number
      projectId?: number | null
      note?: string | null
    }) =>
      api.post<import('./types').Equipment>(`/api/equipment/${id}/reservations`, {
        fromDate,
        days,
        projectId,
        note
      }),
    onSuccess: () => equipmentInvalidation(client)
  })
}

export function useCancelEquipmentReservation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reservationId }: { id: number; reservationId: number }) =>
      api.delete<import('./types').Equipment>(`/api/equipment/${id}/reservations/${reservationId}`),
    onSuccess: () => equipmentInvalidation(client)
  })
}

/** Lægger et foto paa grejet. Billedet skaleres i browseren foer upload. */
export function useUploadEquipmentPhoto() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, file, fileName }: { id: number; file: Blob; fileName: string }) =>
      uploadFile<import('./types').Equipment>(`/api/equipment/${id}/photo`, file, fileName),
    onSuccess: () => equipmentInvalidation(client)
  })
}

export function useDeleteEquipmentPhoto() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<import('./types').Equipment>(`/api/equipment/${id}/photo`),
    onSuccess: () => equipmentInvalidation(client)
  })
}

export function useReturnEquipment() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, note }: { id: number; note?: string | null }) =>
      api.post<import('./types').Equipment>(`/api/equipment/${id}/return`, { note }),
    onSuccess: () => equipmentInvalidation(client)
  })
}

/**
 * Skriver en post i vaerktoejets log (beslutning 096): "service" - hvad der blev
 * lavet og skiftet - eller "inspection", eftersynet der rykker naeste dato.
 *
 * Svaret er selve posten, saa billeder kan laegges paa den med det samme.
 */
export function useAddEquipmentInspection() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      kind,
      performedOn,
      performedBy,
      isApproved,
      workPerformed,
      partsReplaced,
      notes
    }: {
      id: number
      kind?: 'service' | 'inspection'
      performedOn?: string | null
      performedBy?: string | null
      isApproved?: boolean
      workPerformed?: string | null
      partsReplaced?: string | null
      notes?: string | null
    }) =>
      api.post<import('./types').EquipmentInspection>(`/api/equipment/${id}/inspections`, {
        kind,
        performedOn,
        performedBy,
        isApproved: isApproved ?? true,
        workPerformed,
        partsReplaced,
        notes
      }),
    onSuccess: () => equipmentInvalidation(client)
  })
}

/** Laegger et billede paa en post i loggen. Billedet skaleres i browseren. */
export function useAddEquipmentLogPhoto() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      entryId,
      file,
      fileName,
      caption
    }: {
      id: number
      entryId: number
      file: Blob
      fileName: string
      caption?: string
    }) =>
      uploadFile<import('./types').EquipmentLogPhoto>(
        `/api/equipment/${id}/log/${entryId}/photos`,
        file,
        fileName,
        { caption }
      ),
    onSuccess: () => equipmentInvalidation(client)
  })
}

// ---------------------------------------------------------------------------
// Fejl- og mangelliste (beslutning 058)
// ---------------------------------------------------------------------------

export const useDefects = (projectId: number) =>
  useQuery({
    queryKey: ['defects', projectId],
    queryFn: () => api.get<import('./types').DefectList>(`/api/projects/${projectId}/defects`),
    // Modulet kan vaere slaaet fra - saa svarer API-et 404, og kortet skjules.
    retry: false
  })

/** Alt der aendrer en mangel roerer ogsaa afleveringsmappens forhaandsvisning. */
function defectInvalidation(client: ReturnType<typeof useQueryClient>, projectId: number) {
  void client.invalidateQueries({ queryKey: ['defects', projectId] })
  void client.invalidateQueries({ queryKey: ['handover', projectId] })
  void client.invalidateQueries({ queryKey: ['activity'] })
}

export function useSaveDefect(projectId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id?: number; body: import('./types').DefectInput }) =>
      id
        ? api.put<import('./types').Defect>(`/api/projects/${projectId}/defects/${id}`, body)
        : api.post<import('./types').Defect>(`/api/projects/${projectId}/defects`, body),
    onSuccess: () => defectInvalidation(client, projectId)
  })
}

export function useFixDefect(projectId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      fixedOn,
      note,
      fixedPhotoFileId
    }: {
      id: number
      fixedOn?: string | null
      note?: string | null
      fixedPhotoFileId?: number | null
    }) =>
      api.post<import('./types').Defect>(`/api/projects/${projectId}/defects/${id}/fix`, {
        fixedOn,
        note,
        fixedPhotoFileId
      }),
    onSuccess: () => defectInvalidation(client, projectId)
  })
}

export function useReopenDefect(projectId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.post<import('./types').Defect>(`/api/projects/${projectId}/defects/${id}/reopen`, {}),
    onSuccess: () => defectInvalidation(client, projectId)
  })
}

export function useRejectDefect(projectId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      api.post<import('./types').Defect>(`/api/projects/${projectId}/defects/${id}/reject`, { reason }),
    onSuccess: () => defectInvalidation(client, projectId)
  })
}

export function useDeleteDefect(projectId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/projects/${projectId}/defects/${id}`),
    onSuccess: () => defectInvalidation(client, projectId)
  })
}

// ---------------------------------------------------------------------------
// Porte og nøglebokse på sagen (beslutning 088)
// ---------------------------------------------------------------------------

/** Listen er UDEN koder. En kode hentes kun med useRevealAccessCode og lægges aldrig i cachen. */
export const useProjectAccessCodes = (projectId: number) =>
  useQuery({
    queryKey: ['access-codes', projectId],
    queryFn: () => api.get<import('./types').ProjectAccessCode[]>(`/api/projects/${projectId}/access-codes`)
  })

function accessCodeInvalidation(client: ReturnType<typeof useQueryClient>, projectId: number) {
  void client.invalidateQueries({ queryKey: ['access-codes', projectId] })
  void client.invalidateQueries({ queryKey: ['activity'] })
}

export function useSaveAccessCode(projectId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id?: number; body: import('./types').ProjectAccessCodeInput }) =>
      id
        ? api.put<import('./types').ProjectAccessCode>(`/api/projects/${projectId}/access-codes/${id}`, body)
        : api.post<import('./types').ProjectAccessCode>(`/api/projects/${projectId}/access-codes`, body),
    onSuccess: () => accessCodeInvalidation(client, projectId)
  })
}

export function useDeleteAccessCode(projectId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/projects/${projectId}/access-codes/${id}`),
    onSuccess: () => accessCodeInvalidation(client, projectId)
  })
}

/** "Vis kode". Skrives i sagens aktivitetslog på serveren. */
export function useRevealAccessCode(projectId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ code: string }>(`/api/projects/${projectId}/access-codes/${id}/reveal`, {}),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['activity'] })
  })
}

// ---------------------------------------------------------------------------
// Garantier og reklamationer (beslutning 059)
// ---------------------------------------------------------------------------

export const useWarranties = (options?: {
  search?: string
  customerId?: number
  projectId?: number
  expiringOnly?: boolean
  includeExpired?: boolean
}) => {
  const params = new URLSearchParams()
  if (options?.search) params.set('search', options.search)
  if (options?.customerId) params.set('customerId', String(options.customerId))
  if (options?.projectId) params.set('projectId', String(options.projectId))
  if (options?.expiringOnly) params.set('expiringOnly', 'true')
  if (options?.includeExpired) params.set('includeExpired', 'true')
  const query = params.toString()

  return useQuery({
    queryKey: ['warranties', query],
    queryFn: () => api.get<import('./types').Warranty[]>(`/api/warranties${query ? `?${query}` : ''}`)
  })
}

export const useWarrantySummary = () =>
  useQuery({
    queryKey: ['warranties', 'summary'],
    queryFn: () => api.get<import('./types').WarrantySummary>('/api/warranties/summary')
  })

export const useWarrantyTypes = () =>
  useQuery({
    queryKey: ['warranty-types'],
    queryFn: () => api.get<import('./types').WarrantyType[]>('/api/warranties/types'),
    staleTime: 60 * 60 * 1000
  })

export const useWarrantyClaims = (id: number | null) =>
  useQuery({
    queryKey: ['warranties', 'claims', id],
    queryFn: () => api.get<import('./types').WarrantyClaim[]>(`/api/warranties/${id}/claims`),
    enabled: id !== null
  })

function warrantyInvalidation(client: ReturnType<typeof useQueryClient>) {
  void client.invalidateQueries({ queryKey: ['warranties'] })
  void client.invalidateQueries({ queryKey: ['activity'] })
}

export function useSaveWarranty() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id?: number; body: import('./types').WarrantyInput }) =>
      id
        ? api.put<import('./types').Warranty>(`/api/warranties/${id}`, body)
        : api.post<import('./types').Warranty>('/api/warranties', body),
    onSuccess: () => warrantyInvalidation(client)
  })
}

export function useDeleteWarranty() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/warranties/${id}`),
    onSuccess: () => warrantyInvalidation(client)
  })
}

export function useAddClaim() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      warrantyId,
      title,
      description,
      reportedOn
    }: {
      warrantyId: number
      title: string
      description?: string | null
      reportedOn?: string | null
    }) =>
      api.post<import('./types').WarrantyClaim>(`/api/warranties/${warrantyId}/claims`, {
        title,
        description,
        reportedOn
      }),
    onSuccess: () => warrantyInvalidation(client)
  })
}

export function useResolveClaim() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      warrantyId,
      claimId,
      status,
      outcome
    }: {
      warrantyId: number
      claimId: number
      status: string
      outcome?: string | null
    }) =>
      api.post<import('./types').WarrantyClaim>(
        `/api/warranties/${warrantyId}/claims/${claimId}/resolve`,
        { status, outcome }
      ),
    onSuccess: () => warrantyInvalidation(client)
  })
}

// ---------------------------------------------------------------------------
// Akut service og beskeder til marken (beslutning 060)
// ---------------------------------------------------------------------------

/**
 * Mine egne aabne beskeder - den, banneret oeverst i appen lever af.
 *
 * Hentes ogsaa med jaevne mellemrum: et akut udkald maa ikke vente paa, at
 * brugeren tilfaeldigvis skifter side. Realtidsbeskeden invaliderer den ogsaa,
 * men et interval er den sikre bund, naar forbindelsen har vaeret vaek.
 */
export const useMyFieldMessages = () =>
  useQuery({
    queryKey: ['field-messages', 'mine'],
    queryFn: () => api.get<import('./types').FieldMessage[]>('/api/field-messages/mine'),
    refetchInterval: 60_000,
    // Modulet kan vaere slaaet fra - saa svarer API-et 404, og banneret skjules.
    retry: false
  })

export const useFieldMessages = (options?: { includeClosed?: boolean; days?: number }) => {
  const params = new URLSearchParams()
  if (options?.includeClosed) params.set('includeClosed', 'true')
  if (options?.days) params.set('days', String(options.days))
  const query = params.toString()

  return useQuery({
    queryKey: ['field-messages', 'all', query],
    queryFn: () =>
      api.get<import('./types').FieldMessage[]>(`/api/field-messages${query ? `?${query}` : ''}`),
    retry: false
  })
}

/** Hvem der er paa sagen i dag - forslag til modtagere af en jobbesked. */
export const useUsersOnJob = (projectId: number | null) =>
  useQuery({
    queryKey: ['field-messages', 'on-job', projectId],
    queryFn: () => api.get<import('./types').UserRef[]>(`/api/field-messages/on-job/${projectId}`),
    enabled: projectId !== null
  })

function fieldMessageInvalidation(client: ReturnType<typeof useQueryClient>) {
  void client.invalidateQueries({ queryKey: ['field-messages'] })
  void client.invalidateQueries({ queryKey: ['notifications'] })
  void client.invalidateQueries({ queryKey: ['activity'] })
}

export function useSendFieldMessage() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: import('./types').FieldMessageSend) =>
      api.post<import('./types').FieldMessage[]>('/api/field-messages', body),
    onSuccess: () => fieldMessageInvalidation(client)
  })
}

export function useAcknowledgeFieldMessage() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.post<import('./types').FieldMessage>(`/api/field-messages/${id}/acknowledge`, {}),
    onSuccess: () => fieldMessageInvalidation(client)
  })
}

export function useOnWayFieldMessage() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.post<import('./types').FieldMessage>(`/api/field-messages/${id}/on-way`, {}),
    onSuccess: () => fieldMessageInvalidation(client)
  })
}

export function useCloseFieldMessage() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      outcome,
      cancelled
    }: {
      id: number
      outcome?: string | null
      cancelled?: boolean
    }) =>
      api.post<import('./types').FieldMessage>(`/api/field-messages/${id}/close`, {
        outcome,
        cancelled: cancelled ?? false
      }),
    onSuccess: () => fieldMessageInvalidation(client)
  })
}

// ---------------------------------------------------------------------------
// Drift
// ---------------------------------------------------------------------------

/** Hvordan det gik, sidst der blev taget sikkerhedskopi. */
export const useBackupStatus = () =>
  useQuery({
    queryKey: ['operations', 'backup'],
    queryFn: () => api.get<import('./types').BackupStatus>('/api/operations/backup')
  })

export const useDatabaseStatus = () =>
  useQuery({
    queryKey: ['operations', 'database'],
    queryFn: () => api.get<import('./types').DatabaseStatus>('/api/operations/database')
  })

// ---------------------------------------------------------------------------
// Moduler og måleinstrumenter
// ---------------------------------------------------------------------------

/**
 * Hvilke moduler der er slået til.
 *
 * Hentes af hele brugerfladen, så menuen kan skjule det, der er slukket. Holdes
 * i cachen et stykke tid: den ændrer sig sjældent, og et kald pr. sideskift ville
 * være spild.
 */
export const useModules = () =>
  useQuery({
    queryKey: ['modules'],
    queryFn: () => api.get<import('./types').ModuleState[]>('/api/app-settings/modules'),
    staleTime: 5 * 60 * 1000
  })

/** Slår et modul til eller fra. Kun administratorer. */
export function useSetModule() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      api.put<import('./types').ModuleState[]>(`/api/modules/${key}`, { enabled }),
    onSuccess: () => {
      // Menuen og siderne skal rette sig med det samme.
      void client.invalidateQueries({ queryKey: ['modules'] })
      void client.invalidateQueries({ queryKey: ['activity'] })
    }
  })
}

export const useInstruments = (params: { search?: string; dueOnly?: boolean; includeInactive?: boolean } = {}) =>
  useQuery({
    queryKey: ['instruments', params],
    queryFn: () => {
      const q = new URLSearchParams()
      if (params.search) q.set('search', params.search)
      if (params.dueOnly) q.set('dueOnly', 'true')
      if (params.includeInactive) q.set('includeInactive', 'true')
      return api.get<import('./types').Instrument[]>(`/api/instruments?${q}`)
    }
  })

export const useInstrument = (id: number | null) =>
  useQuery({
    queryKey: ['instruments', id],
    enabled: id !== null,
    queryFn: () => api.get<import('./types').InstrumentDetail>(`/api/instruments/${id}`)
  })

export function useSaveInstrument() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id?: number; body: Record<string, unknown> }) =>
      id
        ? api.put<import('./types').InstrumentDetail>(`/api/instruments/${id}`, body)
        : api.post<import('./types').InstrumentDetail>('/api/instruments', body),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['instruments'] })
  })
}

export function useAddCalibration(instrumentId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<import('./types').InstrumentDetail>(`/api/instruments/${instrumentId}/calibrations`, body),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['instruments'] })
  })
}

export function useDeleteInstrument() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/instruments/${id}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['instruments'] })
  })
}

/** Vælger måleinstrumentet på et servicebesøg. Null fjerner det. */
export function useSetVisitInstrument(visitId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (instrumentId: number | null) =>
      api.put<unknown>(`/api/service-visits/${visitId}/instrument`, { instrumentId }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['service-visits'] })
  })
}

// ---------------------------------------------------------------------------
// Kort
// ---------------------------------------------------------------------------

/** Kortet over alle kunder og enheder med egen adresse (beslutning 109). */
export const useCustomerMap = (enabled = true) =>
  useQuery({
    queryKey: ['map', 'customers'],
    queryFn: () => api.get<import('./types').MapData>('/api/map/customers'),
    enabled
  })

export const useMap = (includeService = true) =>
  useQuery({
    queryKey: ['map', includeService],
    queryFn: () =>
      api.get<import('./types').MapData>(`/api/map?includeService=${includeService}`)
  })

/**
 * Åbne sager i nærheden af, hvor medarbejderen allerede skal hen den dag.
 *
 * Slået fra når der ikke er valgt en medarbejder - der er intet at regne ud fra.
 */
export const useNearby = (userId: number | null, date: string, radiusKm: number) =>
  useQuery({
    queryKey: ['map', 'nearby', userId, date, radiusKm],
    enabled: userId !== null,
    queryFn: () =>
      api.get<import('./types').NearbySuggestion>(
        `/api/map/nearby?userId=${userId}&date=${date}&radiusKm=${radiusKm}`
      )
  })

/** Slå adresserne op nu i stedet for at vente på baggrundsjobbet. */
export function useRunGeocoding() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<import('./types').GeocodeRun>('/api/map/geocode', {}),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['map'] })
  })
}

// ---------------------------------------------------------------------------
// Systemindstillinger (Superadmin)
// ---------------------------------------------------------------------------

/** Hvad alle brugere må vide om Teams-opsætningen (om den er slået til og auto-opretter). */
export const useFilesSettings = () =>
  useQuery({
    queryKey: ['app-settings', 'files'],
    queryFn: () => api.get<import('./types').FilesSettings>('/api/app-settings/files'),
    staleTime: 5 * 60 * 1000
  })

export const useMicrosoft365Settings = () =>
  useQuery({
    queryKey: ['settings', 'microsoft365'],
    queryFn: () => api.get<import('./types').Microsoft365Settings>('/api/settings/microsoft365')
  })

export function useSaveMicrosoft365Settings() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.put<import('./types').Microsoft365Settings>('/api/settings/microsoft365', body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['settings'] })
  })
}

export function useTestMicrosoft365() {
  return useMutation({
    mutationFn: () => api.post<import('./types').ConnectionTest>('/api/settings/microsoft365/test')
  })
}

/** Kontrollerer om appen faktisk kan nå teamets dokumentbibliotek i SharePoint. */
export function useTestFileAccess() {
  return useMutation({
    mutationFn: () => api.post<import('./types').ConnectionTest>('/api/settings/microsoft365/test-files')
  })
}

export function useRunDirectorySync() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<import('./types').DirectorySyncResult>('/api/settings/microsoft365/sync'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['settings'] })
      void client.invalidateQueries({ queryKey: ['users'] })
    }
  })
}

// --- Abonnementer (beslutning 031) ---

export const useSubscriptions = (customerId?: number | null, includeEnded = false) =>
  useQuery({
    queryKey: ['subscriptions', customerId ?? null, includeEnded],
    queryFn: () => {
      const params = new URLSearchParams()
      if (customerId) params.set('customerId', String(customerId))
      if (includeEnded) params.set('includeEnded', 'true')
      return api.get<import('./types').Subscription[]>(`/api/subscriptions?${params}`)
    }
  })

export const useSubscription = (id: number) =>
  useQuery({
    queryKey: ['subscriptions', id],
    queryFn: () => api.get<import('./types').SubscriptionDetail>(`/api/subscriptions/${id}`),
    enabled: id > 0
  })

export function useSaveSubscription() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id?: number; body: Record<string, unknown> }) =>
      id
        ? api.put<import('./types').Subscription>(`/api/subscriptions/${id}`, body)
        : api.post<import('./types').Subscription>('/api/subscriptions', body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['subscriptions'] })
  })
}

export function useDeleteSubscription() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/subscriptions/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['subscriptions'] })
  })
}

/** Faktureringsgrundlagene - dem der venter på at blive sendt til økonomisystemet. */
export const useSubscriptionInvoices = (status?: string | null) =>
  useQuery({
    queryKey: ['subscription-invoices', status ?? null],
    queryFn: () => {
      const params = new URLSearchParams()
      if (status) params.set('status', status)
      return api.get<import('./types').SubscriptionInvoice[]>(`/api/subscriptions/invoices?${params}`)
    }
  })

/** Danner de grundlag der forfalder nu. Kører også når automatisk fakturering er slået fra. */
export function useRunBilling() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<import('./types').BillingRunResult>('/api/subscriptions/billing/run'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['subscriptions'] })
      void client.invalidateQueries({ queryKey: ['subscription-invoices'] })
    }
  })
}

export function useCancelSubscriptionInvoice() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post(`/api/subscriptions/invoices/${id}/cancel`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['subscription-invoices'] })
  })
}

/** Til/fra og klokkeslæt for den automatiske fakturering. */
export const useBillingSettings = () =>
  useQuery({
    queryKey: ['subscriptions', 'billing-settings'],
    queryFn: () => api.get<import('./types').BillingSettings>('/api/subscriptions/billing/settings')
  })

export function useSaveBillingSettings() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: import('./types').BillingSettings) =>
      api.put<import('./types').BillingSettings>('/api/subscriptions/billing/settings', body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['subscriptions'] })
  })
}

export const useExportStatus = () =>
  useQuery({
    queryKey: ['subscriptions', 'export-status'],
    queryFn: () => api.get<import('./types').ExportStatus>('/api/subscriptions/export/status')
  })

/** Opsætningen af deadline-varsler. Administrator er nok - det er en arbejdsgang, ikke drift. */
export const useDeadlineAlertSettings = () =>
  useQuery({
    queryKey: ['settings', 'deadlines'],
    queryFn: () => api.get<import('./types').DeadlineAlertSettings>('/api/settings/deadlines')
  })

export function useSaveDeadlineAlertSettings() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: import('./types').DeadlineAlertSettings) =>
      api.put<import('./types').DeadlineAlertSettings>('/api/settings/deadlines', body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['settings'] })
  })
}

/** Sender dagens varsler med det samme - også hvis de er slået fra. */
export function useRunDeadlineAlerts() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<import('./types').DeadlineAlertResult>('/api/settings/deadlines/run'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['notifications'] })
    }
  })
}

/** Morgenbeskeden med dagens plan (beslutning 081). Kun Administrator. */
export const useMorningBriefSettings = () =>
  useQuery({
    queryKey: ['settings', 'morning-brief'],
    queryFn: () => api.get<import('./types').MorningBriefSettings>('/api/settings/morning-brief')
  })

export function useSaveMorningBriefSettings() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: import('./types').MorningBriefSettings) =>
      api.put<import('./types').MorningBriefSettings>('/api/settings/morning-brief', body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['settings'] })
  })
}

/** Sender morgenbeskeden med det samme - også hvis den er slået fra. */
export function useRunMorningBrief() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<import('./types').MorningBriefResult>('/api/settings/morning-brief/run'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['notifications'] })
    }
  })
}

export const useRoles = () =>
  useQuery({ queryKey: ['roles'], queryFn: () => api.get<import('./types').Role[]>('/api/roles') })

export function useSetRoleEntraGroup(roleId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (entraGroupId: string | null) =>
      api.put<import('./types').Role>(`/api/roles/${roleId}/entra-group`, { entraGroupId }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['roles'] })
  })
}

// ---------------------------------------------------------------------------
// Kunder
// ---------------------------------------------------------------------------

export const useCustomers = (search?: string, includeInactive = false) =>
  useQuery({
    queryKey: ['customers', search ?? '', includeInactive],
    queryFn: () =>
      api.get<import('./types').Customer[]>(
        `/api/customers${toQueryString({ search, includeInactive: includeInactive || undefined })}`
      ),
    staleTime: 60_000
  })

export const useCustomer = (id: number) =>
  useQuery({
    queryKey: ['customers', id],
    queryFn: () => api.get<import('./types').CustomerDetail>(`/api/customers/${id}`),
    enabled: id > 0
  })

export function useSaveCustomer(id?: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      id
        ? api.put<import('./types').Customer>(`/api/customers/${id}`, body)
        : api.post<import('./types').Customer>('/api/customers', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['customers'] })
      void client.invalidateQueries({ queryKey: ['projects'] })
    }
  })
}

/** Sletter kunden permanent - kun Superadmin (beslutning 079). */
export function useDeleteCustomer() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/customers/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['customers'] })
      void client.invalidateQueries({ queryKey: ['projects'] })
    }
  })
}

// ---------------------------------------------------------------------------
// Procedureskabeloner
// ---------------------------------------------------------------------------

export const useProjectTemplates = (includeInactive = false) =>
  useQuery({
    queryKey: ['project-templates', includeInactive],
    queryFn: () =>
      api.get<import('./types').ProjectTemplate[]>(
        `/api/project-templates${includeInactive ? '?includeInactive=true' : ''}`
      ),
    staleTime: 60_000
  })

export function useSaveProjectTemplate(id?: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      id
        ? api.put<import('./types').ProjectTemplate>(`/api/project-templates/${id}`, body)
        : api.post<import('./types').ProjectTemplate>('/api/project-templates', body),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['project-templates'] })
      client.invalidateQueries({ queryKey: ['procedures'] })
    }
  })
}

export function useTemplateAction() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, action }: { id: number; action: 'duplicate' | 'delete' }) => {
      if (action === 'duplicate') {
        await api.post<import('./types').ProjectTemplate>(`/api/project-templates/${id}/duplicate`)
      } else {
        await api.delete<void>(`/api/project-templates/${id}`)
      }
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['project-templates'] })
      // Antal skabeloner pr. procedure ændrer sig.
      client.invalidateQueries({ queryKey: ['procedures'] })
    }
  })
}

/** Procedurer - typerne af sager, hver med en eller flere skabeloner (beslutning 141). */
export const useProcedures = (includeInactive = false) =>
  useQuery({
    queryKey: ['procedures', includeInactive],
    queryFn: () =>
      api.get<import('./types').Procedure[]>(`/api/procedures${includeInactive ? '?includeInactive=true' : ''}`),
    staleTime: 60_000
  })

export function useSaveProcedure() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: number; name: string; description: string | null; isActive: boolean; sortOrder: number }) =>
      id
        ? api.put<import('./types').Procedure>(`/api/procedures/${id}`, body)
        : api.post<import('./types').Procedure>('/api/procedures', body),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['procedures'] })
      client.invalidateQueries({ queryKey: ['project-templates'] })
    }
  })
}

export function useDeleteProcedure() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/procedures/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['procedures'] })
  })
}

// ---------------------------------------------------------------------------
// Enheder (batterier, tavler, anlæg) og service
// ---------------------------------------------------------------------------

export interface DeviceFilter {
  search?: string
  customerId?: number
  projectId?: number
  /** Kun enheder, denne servicesag handler om (beslutning 076). */
  serviceProjectId?: number
  /** ISO-dato: kun enheder med næste service før denne - forsidens overblik. */
  dueBefore?: string
  onServicePlan?: boolean
  includeInactive?: boolean
}

export const useDevices = (filter: DeviceFilter = {}, enabled = true) =>
  useQuery({
    queryKey: ['devices', filter],
    enabled,
    queryFn: () => {
      const params = new URLSearchParams()
      if (filter.search) params.set('search', filter.search)
      if (filter.customerId) params.set('customerId', String(filter.customerId))
      if (filter.projectId) params.set('projectId', String(filter.projectId))
      if (filter.serviceProjectId) params.set('serviceProjectId', String(filter.serviceProjectId))
      if (filter.dueBefore) params.set('dueBefore', filter.dueBefore)
      if (filter.onServicePlan) params.set('onServicePlan', 'true')
      if (filter.includeInactive) params.set('includeInactive', 'true')

      const query = params.toString()
      return api.get<import('./types').Device[]>(`/api/devices${query ? `?${query}` : ''}`)
    }
  })

export const useDevice = (id: number) =>
  useQuery({
    queryKey: ['devices', 'detail', id],
    queryFn: () => api.get<import('./types').DeviceDetail>(`/api/devices/${id}`),
    enabled: Number.isFinite(id) && id > 0
  })

/**
 * Enhedens historik: sidste eftersyn, målingerne over tid, fotoene, garantierne
 * og de åbne mangler på installationssagen (beslutning 062).
 */
export const useDeviceHistory = (id: number) =>
  useQuery({
    queryKey: ['devices', 'history', id],
    queryFn: () => api.get<import('./types').DeviceHistory>(`/api/devices/${id}/history`),
    enabled: Number.isFinite(id) && id > 0
  })

/** Slår en enhed op ud fra koden i QR-mærkatet. Bruges af scanneren. */
export const useDeviceByCode = (code: string | null) =>
  useQuery({
    queryKey: ['devices', 'by-code', code],
    queryFn: () => api.get<import('./types').DeviceDetail>(`/api/devices/by-code/${encodeURIComponent(code!)}`),
    enabled: Boolean(code),
    retry: false
  })

export function useSaveDevice(id?: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      id
        ? api.put<import('./types').DeviceDetail>(`/api/devices/${id}`, body)
        : api.post<import('./types').DeviceDetail>('/api/devices', body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['devices'] })
  })
}

export function useDeleteDevice() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/devices/${id}`),
    // Den slettede enheds egne forespørgsler (detalje, historik, filer) hentes IKKE igen -
    // siden står stadig åben, til navigate() har skiftet side, og ville ellers få 404.
    onSuccess: (_, id) =>
      client.invalidateQueries({
        queryKey: ['devices'],
        predicate: q => !q.queryKey.slice(1, 3).includes(id)
      })
  })
}

// ---------------------------------------------------------------------------
// Service: checklister og besøg
// ---------------------------------------------------------------------------

export const useServiceChecklists = (deviceType?: string | null, includeInactive = false) =>
  useQuery({
    queryKey: ['service-checklists', deviceType ?? null, includeInactive],
    queryFn: () => {
      const params = new URLSearchParams()
      if (deviceType) params.set('deviceType', deviceType)
      if (includeInactive) params.set('includeInactive', 'true')

      const query = params.toString()
      return api.get<import('./types').ServiceChecklist[]>(
        `/api/service-checklists${query ? `?${query}` : ''}`
      )
    }
  })

export function useSaveServiceChecklist(id?: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      id
        ? api.put<import('./types').ServiceChecklist>(`/api/service-checklists/${id}`, body)
        : api.post<import('./types').ServiceChecklist>('/api/service-checklists', body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['service-checklists'] })
  })
}

export function useDeleteServiceChecklist() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/service-checklists/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['service-checklists'] })
  })
}

/** Servicebesøg. Uden deviceId hentes alle, nyeste først. */
export const useServiceVisits = (deviceId?: number) =>
  useQuery({
    queryKey: ['service-visits', deviceId ?? null],
    queryFn: () =>
      api.get<import('./types').ServiceVisit[]>(
        `/api/service-visits${deviceId ? `?deviceId=${deviceId}` : ''}`
      )
  })

export const useServiceVisit = (id: number) =>
  useQuery({
    queryKey: ['service-visits', 'detail', id],
    queryFn: () => api.get<import('./types').ServiceVisitDetail>(`/api/service-visits/${id}`),
    enabled: Number.isFinite(id) && id > 0
  })

/** Starter et besøg - eller fortsætter et der allerede er i gang på enheden. */
export function useStartServiceVisit() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: { deviceId: number; checklistId?: number | null }) =>
      api.post<import('./types').ServiceVisitDetail>('/api/service-visits', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['service-visits'] })
      void client.invalidateQueries({ queryKey: ['devices'] })
    }
  })
}

/** Alt der ændrer et igangværende besøg returnerer hele besøget igen. */
export function useServiceVisitActions(visitId: number) {
  const client = useQueryClient()

  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['service-visits'] })
    void client.invalidateQueries({ queryKey: ['devices'] })
  }

  const answerItem = useMutation({
    mutationFn: ({ itemId, ...body }: { itemId: number; result?: string | null; value?: number | null; note?: string | null }) =>
      api.put<import('./types').ServiceVisitDetail>(`/api/service-visits/${visitId}/items/${itemId}`, body),
    onSuccess: refresh
  })

  const saveNotes = useMutation({
    mutationFn: (notes: string) =>
      api.put<import('./types').ServiceVisitDetail>(`/api/service-visits/${visitId}/notes`, { notes }),
    onSuccess: refresh
  })

  const addPhoto = useMutation({
    mutationFn: ({ file, fileName, kind }: { file: Blob; fileName: string; kind: string }) =>
      uploadFile<import('./types').ServiceVisitDetail>(
        `/api/service-visits/${visitId}/photos`,
        file,
        fileName,
        { kind }
      ),
    onSuccess: refresh
  })

  const complete = useMutation({
    mutationFn: (body: { signedByName: string; signatureDataUrl: string; nextServiceDate?: string | null }) =>
      api.post<import('./types').ServiceVisitDetail>(`/api/service-visits/${visitId}/complete`, body),
    onSuccess: refresh
  })

  const cancel = useMutation({
    mutationFn: () => api.post<import('./types').ServiceVisitDetail>(`/api/service-visits/${visitId}/cancel`),
    onSuccess: refresh
  })

  return { answerItem, saveNotes, addPhoto, complete, cancel }
}

// ---------------------------------------------------------------------------
// Filer og fotos på sager (ligger i sagens mappe i SharePoint, beslutning 033)
// ---------------------------------------------------------------------------

export const useProjectFiles = (projectId: number) =>
  useQuery({
    queryKey: ['projects', projectId, 'files'],
    queryFn: () => api.get<import('./types').ProjectFile[]>(`/api/projects/${projectId}/files`)
  })

export function useUploadProjectFile(projectId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ file, fileName, category, caption }: UploadProjectFile) =>
      uploadFile<import('./types').ProjectFile>(
        `/api/projects/${projectId}/files`,
        file,
        fileName,
        { category, caption }
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: ['projects', projectId, 'files'] })
  })
}

export function useUpdateProjectFile(projectId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ fileId, ...body }: { fileId: number; category?: string; caption?: string }) =>
      api.put<import('./types').ProjectFile>(`/api/projects/${projectId}/files/${fileId}`, body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['projects', projectId, 'files'] })
  })
}

export function useDeleteProjectFile(projectId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (fileId: number) => api.delete<void>(`/api/projects/${projectId}/files/${fileId}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['projects', projectId, 'files'] })
  })
}

interface UploadProjectFile {
  file: Blob
  fileName: string
  category: string
  caption?: string
}

export function useApplyTemplate(projectId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (templateId: number) =>
      api.post<import('./types').ProjectDetail>(`/api/projects/${projectId}/apply-template/${templateId}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['projects'] })
      void client.invalidateQueries({ queryKey: ['tasks'] })
    }
  })
}

// ---------- Manualer (beslutning 063) ----------

/**
 * Manualbiblioteket. Filtrene bruges baade af selve biblioteket og af de steder,
 * manualerne dukker op i en sammenhaeng: enhedens side (deviceType) og sagen
 * (templateId).
 */
export const useManuals = (filters?: {
  search?: string
  categoryId?: number | null
  deviceType?: string | null
  templateId?: number | null
  includeInactive?: boolean
  enabled?: boolean
}) =>
  useQuery({
    queryKey: ['manuals', filters ?? {}],
    queryFn: () => {
      const params = new URLSearchParams()
      if (filters?.search) params.set('search', filters.search)
      if (filters?.categoryId) params.set('categoryId', String(filters.categoryId))
      if (filters?.deviceType) params.set('deviceType', filters.deviceType)
      if (filters?.templateId) params.set('templateId', String(filters.templateId))
      if (filters?.includeInactive) params.set('includeInactive', 'true')

      const query = params.toString()
      return api.get<import('./types').Manual[]>(`/api/manuals${query ? `?${query}` : ''}`)
    },
    enabled: filters?.enabled ?? true
  })

export const useManual = (id: number) =>
  useQuery({
    queryKey: ['manuals', 'detail', id],
    queryFn: () => api.get<import('./types').ManualDetail>(`/api/manuals/${id}`),
    enabled: Number.isFinite(id) && id > 0
  })

export const useManualCategories = () =>
  useQuery({
    queryKey: ['manuals', 'categories'],
    queryFn: () => api.get<import('./types').ManualCategory[]>('/api/manuals/categories')
  })

export const useSaveManual = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ id, input }: { id?: number; input: import('./types').ManualInput }) =>
      id
        ? api.put<import('./types').ManualDetail>(`/api/manuals/${id}`, input)
        : api.post<import('./types').ManualDetail>('/api/manuals', input),
    onSuccess: () => client.invalidateQueries({ queryKey: ['manuals'] })
  })
}

export const useDeleteManual = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/manuals/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['manuals'] })
  })
}

/** Laegger den faerdige manualfil op - typisk leverandoerens PDF. */
export const useUploadManualFile = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ id, file }: { id: number; file: File }) =>
      uploadFile<import('./types').ManualDetail>(`/api/manuals/${id}/file`, file, file.name),
    onSuccess: () => client.invalidateQueries({ queryKey: ['manuals'] })
  })
}

/** Laegger et billede i en skrevet manual. Svarer med id-et, teksten skal bruge. */
export const useUploadManualImage = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ id, file }: { id: number; file: Blob }) =>
      uploadFile<import('./types').ManualImage>(`/api/manuals/${id}/images`, file, 'billede.jpg'),
    onSuccess: (_data, variables) => {
      void client.invalidateQueries({ queryKey: ['manuals', 'detail', variables.id] })
    }
  })
}

export const useDeleteManualImage = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ id, imageId }: { id: number; imageId: number }) =>
      api.delete(`/api/manuals/${id}/images/${imageId}`),
    onSuccess: (_data, variables) => {
      void client.invalidateQueries({ queryKey: ['manuals', 'detail', variables.id] })
    }
  })
}

export const useSaveManualCategory = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ id, input }: { id?: number; input: import('./types').ManualCategoryInput }) =>
      id
        ? api.put<import('./types').ManualCategory>(`/api/manuals/categories/${id}`, input)
        : api.post<import('./types').ManualCategory>('/api/manuals/categories', input),
    onSuccess: () => client.invalidateQueries({ queryKey: ['manuals'] })
  })
}

export const useDeleteManualCategory = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/manuals/categories/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['manuals'] })
  })
}

// ---------- Lager (beslutning 069) ----------

export const useStockItems = (filters?: {
  search?: string
  category?: string | null
  supplier?: string | null
  includeInactive?: boolean
}) =>
  useQuery({
    queryKey: ['stock', 'items', filters ?? {}],
    queryFn: () => {
      const params = new URLSearchParams()
      if (filters?.search) params.set('search', filters.search)
      if (filters?.category) params.set('category', filters.category)
      if (filters?.supplier) params.set('supplier', filters.supplier)
      if (filters?.includeInactive) params.set('includeInactive', 'true')

      const query = params.toString()
      return api.get<import('./types').StockItem[]>(`/api/stock/items${query ? `?${query}` : ''}`)
    }
  })

export const useStockItem = (id: number) =>
  useQuery({
    queryKey: ['stock', 'items', 'detail', id],
    queryFn: () => api.get<import('./types').StockItem>(`/api/stock/items/${id}`),
    enabled: Number.isFinite(id) && id > 0
  })

export const useStockUnits = () =>
  useQuery({
    queryKey: ['stock', 'units'],
    queryFn: () => api.get<import('./types').StockUnit[]>('/api/stock/items/units'),
    staleTime: 60 * 60 * 1000
  })

export const useStockSuggestions = () =>
  useQuery({
    queryKey: ['stock', 'suggestions'],
    queryFn: () => api.get<import('./types').StockSuggestions>('/api/stock/items/suggestions'),
    staleTime: 5 * 60 * 1000
  })

/**
 * Slaar op paa det, der blev scannet - uanset om det var en QR fra os eller en
 * stregkode fra grossisten. Serveren finder selv ud af hvad den fik.
 */
export const useScanStock = () =>
  useMutation({
    mutationFn: (code: string) =>
      api.get<import('./types').StockScanResult>(`/api/stock/items/scan/${encodeURIComponent(code)}`)
  })

export const useSaveStockItem = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ id, input }: { id?: number; input: import('./types').StockItemInput }) =>
      id
        ? api.put<import('./types').StockItem>(`/api/stock/items/${id}`, input)
        : api.post<import('./types').StockItem>('/api/stock/items', input),
    onSuccess: () => client.invalidateQueries({ queryKey: ['stock'] })
  })
}

export const useDeleteStockItem = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/stock/items/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['stock'] })
  })
}

export const useStockLocations = (filters?: {
  kind?: string | null
  parentId?: number | null
  includeInactive?: boolean
}) =>
  useQuery({
    queryKey: ['stock', 'locations', filters ?? {}],
    queryFn: () => {
      const params = new URLSearchParams()
      if (filters?.kind) params.set('kind', filters.kind)
      if (filters?.parentId) params.set('parentId', String(filters.parentId))
      if (filters?.includeInactive) params.set('includeInactive', 'true')

      const query = params.toString()
      return api.get<import('./types').StockLocation[]>(
        `/api/stock/locations${query ? `?${query}` : ''}`
      )
    }
  })

export const useStockLocation = (id: number) =>
  useQuery({
    queryKey: ['stock', 'locations', 'detail', id],
    queryFn: () => api.get<import('./types').StockLocationDetail>(`/api/stock/locations/${id}`),
    enabled: Number.isFinite(id) && id > 0
  })

/** Hvor en bestemt vare ligger, og hvor meget der er i alt. */
export const useStockForItem = (itemId: number) =>
  useQuery({
    queryKey: ['stock', 'items', itemId, 'stock'],
    queryFn: () => api.get<import('./types').StockItemStock>(`/api/stock/items/${itemId}/stock`),
    enabled: Number.isFinite(itemId) && itemId > 0
  })

export const useStockMovements = (filters?: {
  itemId?: number | null
  locationId?: number | null
  projectId?: number | null
  limit?: number
}) =>
  useQuery({
    queryKey: ['stock', 'movements', filters ?? {}],
    queryFn: () => {
      const params = new URLSearchParams()
      if (filters?.itemId) params.set('itemId', String(filters.itemId))
      if (filters?.locationId) params.set('locationId', String(filters.locationId))
      if (filters?.projectId) params.set('projectId', String(filters.projectId))
      if (filters?.limit) params.set('limit', String(filters.limit))

      const query = params.toString()
      return api.get<import('./types').StockMovement[]>(
        `/api/stock/movements${query ? `?${query}` : ''}`
      )
    }
  })

export const useSaveStockLocation = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ id, input }: { id?: number; input: import('./types').StockLocationInput }) =>
      id
        ? api.put<import('./types').StockLocation>(`/api/stock/locations/${id}`, input)
        : api.post<import('./types').StockLocation>('/api/stock/locations', input),
    onSuccess: () => client.invalidateQueries({ queryKey: ['stock'] })
  })
}

export const useDeleteStockLocation = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/stock/locations/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['stock'] })
  })
}

/** Flytter en vare. Retningen ligger i fra/til, ikke i fortegnet. */
export const useMoveStock = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: import('./types').StockMoveInput) =>
      api.post<import('./types').StockMovement>('/api/stock/move', input),
    onSuccess: () => client.invalidateQueries({ queryKey: ['stock'] })
  })
}

/** Saetter beholdningen til det, der faktisk ligger der. */
export const useCountStock = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({
      locationId,
      itemId,
      counted,
      note
    }: {
      locationId: number
      itemId: number
      counted: number
      note?: string | null
    }) =>
      api.post<import('./types').StockMovement | null>(
        `/api/stock/locations/${locationId}/count`,
        { itemId, counted, note }
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: ['stock'] })
  })
}

// ---------- Lager, etape 3: marken (beslutning 071) ----------

/** Min bil, de andre steder og de aabne sager - i ét kald. */
export const useStockFieldContext = () =>
  useQuery({
    queryKey: ['stock', 'field', 'context'],
    queryFn: () => api.get<import('./types').StockFieldContext>('/api/stock/field/context'),
    staleTime: 60_000
  })

/** Hvad der er gaaet til en sag, lagt sammen pr. vare. */
export const useProjectMaterials = (projectId: number) =>
  useQuery({
    queryKey: ['stock', 'projects', projectId, 'materials'],
    queryFn: () =>
      api.get<import('./types').StockProjectMaterials>(`/api/stock/projects/${projectId}/materials`),
    enabled: projectId > 0
  })

/**
 * Registrerer hele kurven i én handling.
 *
 * **Alt eller intet**: serveren gemmer bundtet i ét SaveChanges, saa enten staar
 * hele turen, eller ogsaa staar intet af den.
 */
export const useStockBatch = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: import('./types').StockBatchInput) =>
      api.post<import('./types').StockBatchResult>('/api/stock/batch', input),
    onSuccess: () => client.invalidateQueries({ queryKey: ['stock'] })
  })
}

// ---------- Lager, etape 4: prisfiler (beslutning 072) ----------

export const usePriceMatchKeys = () =>
  useQuery({
    queryKey: ['prices', 'match-keys'],
    queryFn: () => api.get<import('./types').PriceMatchKeyOption[]>('/api/stock/prices/match-keys'),
    staleTime: Infinity
  })

export const usePriceProfiles = () =>
  useQuery({
    queryKey: ['prices', 'profiles'],
    queryFn: () => api.get<import('./types').PriceImportProfile[]>('/api/stock/prices/profiles')
  })

export const useSavePriceProfile = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ id, input }: { id?: number; input: import('./types').PriceImportProfileInput }) =>
      id
        ? api.put<import('./types').PriceImportProfile>(`/api/stock/prices/profiles/${id}`, input)
        : api.post<import('./types').PriceImportProfile>('/api/stock/prices/profiles', input),
    onSuccess: () => client.invalidateQueries({ queryKey: ['prices'] })
  })
}

export const useDeletePriceProfile = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/stock/prices/profiles/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['prices'] })
  })
}

export const usePriceImports = () =>
  useQuery({
    queryKey: ['prices', 'imports'],
    queryFn: () => api.get<import('./types').PriceImport[]>('/api/stock/prices/imports')
  })

/** Laeser filen og viser, hvad den VILLE goere. Skriver ingen priser. */
export const usePreviewPriceFile = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ file, profileId }: { file: File; profileId: number }) =>
      uploadFile<import('./types').PriceImportDetail>('/api/stock/prices/preview', file, file.name, {
        profileId: String(profileId)
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['prices', 'imports'] })
  })
}

/** Saetter priserne fra en forhaandsvisning. */
export const useApplyPriceImport = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (id: number) =>
      api.post<import('./types').PriceImportDetail>(`/api/stock/prices/imports/${id}/apply`, {}),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['prices'] })
      void client.invalidateQueries({ queryKey: ['stock'] })
    }
  })
}

/** Saetter priserne tilbage, som de var. */
export const useUndoPriceImport = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (id: number) =>
      api.post<import('./types').PriceImportUndoResult>(`/api/stock/prices/imports/${id}/undo`, {}),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['prices'] })
      void client.invalidateQueries({ queryKey: ['stock'] })
    }
  })
}

// ---------- Support: henvendelser (beslutning 073) ----------

export const useSupportTypes = () =>
  useQuery({
    queryKey: ['support', 'types'],
    queryFn: () => api.get<import('./types').SupportTypeOption[]>('/api/support/types'),
    staleTime: Infinity
  })

export const useSupportStates = () =>
  useQuery({
    queryKey: ['support', 'states'],
    queryFn: () => api.get<import('./types').SupportStateOption[]>('/api/support/states'),
    staleTime: Infinity
  })

export const useSupportSummary = (enabled = true) =>
  useQuery({
    queryKey: ['support', 'summary'],
    queryFn: () => api.get<import('./types').SupportSummary>('/api/support/summary'),
    enabled
  })

export const useSupportTickets = (filters?: {
  type?: string
  state?: string
  assigneeId?: number
  customerId?: number
  openOnly?: boolean
}) =>
  useQuery({
    queryKey: ['support', 'tickets', filters ?? {}],
    queryFn: () => {
      const query = new URLSearchParams()
      if (filters?.type) query.set('type', filters.type)
      if (filters?.state) query.set('state', filters.state)
      if (filters?.assigneeId) query.set('assigneeId', String(filters.assigneeId))
      if (filters?.customerId) query.set('customerId', String(filters.customerId))
      if (filters?.openOnly === false) query.set('openOnly', 'false')

      const text = query.toString()
      return api.get<import('./types').SupportTicket[]>(`/api/support${text ? `?${text}` : ''}`)
    }
  })

export const useSupportTicket = (id: number) =>
  useQuery({
    queryKey: ['support', 'ticket', id],
    queryFn: () => api.get<import('./types').SupportTicketDetail>(`/api/support/${id}`),
    enabled: id > 0
  })

export const useCreateSupportTicket = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: import('./types').SupportTicketInput) =>
      api.post<import('./types').SupportTicket>('/api/support', input),
    onSuccess: () => client.invalidateQueries({ queryKey: ['support'] })
  })
}

export const useUpdateSupportTicket = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: import('./types').SupportTicketUpdate }) =>
      api.put<import('./types').SupportTicket>(`/api/support/${id}`, input),
    onSuccess: () => client.invalidateQueries({ queryKey: ['support'] })
  })
}

/** Skriver i traaden. Er den ikke intern, sendes den ogsaa som mail. */
export const useAddSupportMessage = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ id, body, isInternal }: { id: number; body: string; isInternal: boolean }) =>
      api.post<import('./types').SupportMessageResult>(`/api/support/${id}/messages`, { body, isInternal }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['support'] })
  })
}

/** Goer henvendelsen til en sag. Den bliver staaende og peger paa sagen. */
export const useConvertSupportTicket = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      name,
      orderNumber,
      statusId,
      projectManagerId
    }: {
      id: number
      name?: string | null
      /** Påkrævet som på alle nye sager (beslutning 077). */
      orderNumber: string
      statusId?: number | null
      projectManagerId?: number | null
    }) =>
      api.post<import('./types').SupportTicket>(`/api/support/${id}/convert-to-project`, {
        name,
        orderNumber,
        statusId,
        projectManagerId
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['support'] })
      void client.invalidateQueries({ queryKey: ['projects'] })
    }
  })
}

export const useSupportMailSettings = () =>
  useQuery({
    queryKey: ['support', 'mail-settings'],
    queryFn: () => api.get<import('./types').SupportMailSettings>('/api/support/mail-settings')
  })

export const useSaveSupportMailSettings = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: import('./types').SupportMailSettingsInput) =>
      api.put<import('./types').SupportMailSettings>('/api/support/mail-settings', input),
    onSuccess: () => client.invalidateQueries({ queryKey: ['support'] })
  })
}

/** Henter mail nu - ogsaa naar laesningen staar til fra, saa opsaetningen kan proeves af. */
export const useRunSupportMail = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: () => api.post<import('./types').SupportMailRun>('/api/support/mail-settings/run', {}),
    onSuccess: () => client.invalidateQueries({ queryKey: ['support'] })
  })
}

// ---------- Hurtigknappen i marken (beslutning 082) ----------

/**
 * Sagerne knappen foreslaar. Positionen sendes med, naar telefonen kender den -
 * ellers spoerges der uden, og serveren svarer med dagens egne jobs foerst i
 * stedet for en fejl.
 */
export const useQuickNoteTargets = (
  position: { lat: number; lon: number } | null,
  search: string,
  enabled = true
) =>
  useQuery({
    queryKey: ['quick-note', 'targets', position?.lat ?? null, position?.lon ?? null, search],
    queryFn: () => {
      const params = new URLSearchParams()
      if (position) {
        params.set('lat', String(position.lat))
        params.set('lon', String(position.lon))
      }
      if (search.trim()) params.set('q', search.trim())

      return api.get<import('./types').QuickNoteTargets>(`/api/quick-note/targets?${params}`)
    },
    enabled,
    // Manden gaar rundt paa pladsen: et minut gammelt svar er stadig rigtigt nok,
    // og en ny forespoergsel ved hvert tastetryk i soegefeltet er spild.
    staleTime: 60_000
  })

/** Note og foto i ét kald. Fotoet er valgfrit - noten kan staa alene. */
export function useSendQuickNote() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({
      projectId,
      note,
      photo,
      fileName
    }: {
      projectId: number
      note: string
      photo: Blob | null
      fileName: string
    }) =>
      uploadFile<import('./types').QuickNoteResult>('/api/quick-note', photo, fileName, {
        projectId: String(projectId),
        note
      }),
    onSuccess: (result) => {
      void client.invalidateQueries({ queryKey: ['projects', result.projectId] })
      void client.invalidateQueries({ queryKey: ['activity'] })
      void client.invalidateQueries({ queryKey: ['notifications'] })
    }
  })
}

// ---------- Kundens eget sagsnummer (beslutning 083) ----------

/**
 * Koerer kunden allerede en anden sag paa det sagsnummer?
 *
 * Det er en advarsel, ikke en spaerring: en kunde kan sagtens bestille to ting
 * paa én rekvisition. Men er det en tastefejl, skal den fanges her og ikke,
 * naar fakturaen kommer retur.
 */
export const useCaseNumberInUse = (
  number: string,
  customerId: number | null,
  projectId?: number
) =>
  useQuery({
    queryKey: ['projects', 'case-number-in-use', number.trim(), customerId, projectId ?? null],
    queryFn: () => {
      const params = new URLSearchParams({ number: number.trim() })
      if (customerId) params.set('customerId', String(customerId))
      if (projectId) params.set('projectId', String(projectId))

      return api.get<import('./types').CaseNumberInUse>(`/api/projects/case-number-in-use?${params}`)
    },
    // Uden en kunde er der ingen at sammenligne med, og et tomt felt skal ikke
    // spoerge om noget.
    enabled: number.trim().length > 0 && customerId !== null,
    staleTime: 30_000
  })

// ---------------------------------------------------------------------------
// Udskiftede dele paa servicesagen (beslutning 098)
// ---------------------------------------------------------------------------

/** Efter en udskiftet del: sagen, enheden og - hvis den kom fra bilen - lageret. */
function replacedPartInvalidation(client: ReturnType<typeof useQueryClient>) {
  void client.invalidateQueries({ queryKey: ['replaced-parts'] })
  void client.invalidateQueries({ queryKey: ['stock'] })
  void client.invalidateQueries({ queryKey: ['activity'] })
}

export const useReplacedParts = (projectId: number, enabled = true) =>
  useQuery({
    queryKey: ['replaced-parts', 'project', projectId],
    queryFn: () => api.get<import('./types').ReplacedPart[]>(`/api/projects/${projectId}/replaced-parts`),
    enabled: enabled && projectId > 0
  })

export const useDeviceReplacedParts = (deviceId: number, enabled = true) =>
  useQuery({
    queryKey: ['replaced-parts', 'device', deviceId],
    queryFn: () => api.get<import('./types').ReplacedPart[]>(`/api/devices/${deviceId}/replaced-parts`),
    enabled: enabled && deviceId > 0
  })

export function useAddReplacedPart(projectId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: import('./types').ReplacedPartInput) =>
      api.post<import('./types').ReplacedPart>(`/api/projects/${projectId}/replaced-parts`, input),
    onSuccess: () => replacedPartInvalidation(client)
  })
}

export function useRemoveReplacedPart(projectId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/projects/${projectId}/replaced-parts/${id}`),
    onSuccess: () => replacedPartInvalidation(client)
  })
}

/**
 * Laegger et billede paa en udskiftet del - i SharePoint (beslutning 112). Billedet skaleres i browseren.
 * `kind`: godkendelsen, billedet efter udskiftningen, eller et billede uden nærmere angivelse.
 */
export function useAddReplacedPartPhoto(projectId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      file,
      fileName,
      kind
    }: {
      id: number
      file: Blob
      fileName: string
      kind: import('./types').ReplacedPartPhotoKind
    }) =>
      uploadFile<import('./types').ReplacedPartPhoto>(
        `/api/projects/${projectId}/replaced-parts/${id}/photos`,
        file,
        fileName,
        { kind }
      ),
    onSuccess: () => replacedPartInvalidation(client)
  })
}

/** Fjerner et billede fra en udskiftet del. Filen havner i SharePoints papirkurv (beslutning 112). */
export function useDeleteReplacedPartPhoto(projectId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, photoId }: { id: number; photoId: number }) =>
      api.delete<void>(`/api/projects/${projectId}/replaced-parts/${id}/photos/${photoId}`),
    onSuccess: () => replacedPartInvalidation(client)
  })
}

/** Retter serienumrene på en udskiftet del - den udtagne og den nye (beslutning 112). */
export function useUpdateReplacedPartSerials(projectId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, oldSerialNumber, newSerialNumber }: { id: number; oldSerialNumber: string; newSerialNumber: string }) =>
      api.put<import('./types').ReplacedPart>(`/api/projects/${projectId}/replaced-parts/${id}/serials`, {
        oldSerialNumber,
        newSerialNumber
      }),
    onSuccess: () => replacedPartInvalidation(client)
  })
}

// ---------------------------------------------------------------------------
// Intranettet (beslutning 100)
// ---------------------------------------------------------------------------

import type {
  IntranetEvent,
  IntranetEventInput,
  IntranetEventKind,
  IntranetLink,
  IntranetLinkInput,
  IntranetPhoto,
  IntranetPost,
  IntranetPostInput,
  IntranetPostReads
} from './types'

/** Efter en aendring paa intranettet: siden selv, beskederne og loggen. */
function intranetInvalidation(client: ReturnType<typeof useQueryClient>) {
  void client.invalidateQueries({ queryKey: ['intranet'] })
  void client.invalidateQueries({ queryKey: ['notifications'] })
  void client.invalidateQueries({ queryKey: ['activity'] })
}

export const useIntranetPosts = (enabled = true) =>
  useQuery({
    queryKey: ['intranet', 'posts'],
    queryFn: () => api.get<IntranetPost[]>('/api/intranet/posts'),
    enabled
  })

export const useIntranetPost = (id: number, enabled = true) =>
  useQuery({
    queryKey: ['intranet', 'posts', id],
    queryFn: () => api.get<IntranetPost>(`/api/intranet/posts/${id}`),
    enabled: enabled && id > 0
  })

export const useIntranetPostReads = (id: number, enabled = true) =>
  useQuery({
    queryKey: ['intranet', 'posts', id, 'reads'],
    queryFn: () => api.get<IntranetPostReads>(`/api/intranet/posts/${id}/reads`),
    enabled: enabled && id > 0
  })

export function useSaveIntranetPost() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id?: number; input: IntranetPostInput }) =>
      id
        ? api.put<IntranetPost>(`/api/intranet/posts/${id}`, input)
        : api.post<IntranetPost>('/api/intranet/posts', input),
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useDeleteIntranetPost() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/intranet/posts/${id}`),
    onSuccess: () => intranetInvalidation(client)
  })
}

/** "Jeg har laest det". Kan ikke fortrydes - den er en oplysning, ikke en tilstand. */
export function useMarkIntranetPostRead() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post<void>(`/api/intranet/posts/${id}/read`, {}),
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useAddIntranetPostPhoto() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, file, fileName, caption }: { id: number; file: Blob; fileName: string; caption?: string }) =>
      uploadFile<IntranetPhoto>(`/api/intranet/posts/${id}/photos`, file, fileName, { caption }),
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useDeleteIntranetPostPhoto() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, photoId }: { id: number; photoId: number }) =>
      api.delete<void>(`/api/intranet/posts/${id}/photos/${photoId}`),
    onSuccess: () => intranetInvalidation(client)
  })
}

export const useIntranetEvents = (includePast = false, enabled = true) =>
  useQuery({
    queryKey: ['intranet', 'events', includePast],
    queryFn: () => api.get<IntranetEvent[]>(`/api/intranet/events${includePast ? '?includePast=true' : ''}`),
    enabled
  })

export const useIntranetEvent = (id: number, enabled = true) =>
  useQuery({
    queryKey: ['intranet', 'events', 'one', id],
    queryFn: () => api.get<IntranetEvent>(`/api/intranet/events/${id}`),
    enabled: enabled && id > 0
  })

export const useIntranetEventKinds = () =>
  useQuery({
    queryKey: ['intranet', 'event-kinds'],
    queryFn: () => api.get<IntranetEventKind[]>('/api/intranet/events/kinds'),
    staleTime: 60 * 60 * 1000
  })

export function useSaveIntranetEvent() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id?: number; input: IntranetEventInput }) =>
      id
        ? api.put<IntranetEvent>(`/api/intranet/events/${id}`, input)
        : api.post<IntranetEvent>('/api/intranet/events', input),
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useRespondToIntranetEvent(id: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: { votes: Record<number, string>; guestCount: number; note: string | null }) =>
      api.post<IntranetEvent>(`/api/intranet/events/${id}/respond`, body),
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useChooseIntranetEventDate(id: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (dateId: number) => api.post<IntranetEvent>(`/api/intranet/events/${id}/choose-date`, { dateId }),
    onSuccess: () => intranetInvalidation(client)
  })
}

export function usePublishIntranetEvent() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post<IntranetEvent>(`/api/intranet/events/${id}/publish`, {}),
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useCancelIntranetEvent() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post<IntranetEvent>(`/api/intranet/events/${id}/cancel`, {}),
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useDeleteIntranetEvent() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/intranet/events/${id}`),
    onSuccess: () => intranetInvalidation(client)
  })
}

export const useIntranetLinks = (includeInactive = false, enabled = true) =>
  useQuery({
    queryKey: ['intranet', 'links', includeInactive],
    queryFn: () =>
      api.get<IntranetLink[]>(`/api/intranet/links${includeInactive ? '?includeInactive=true' : ''}`),
    enabled
  })

export function useSaveIntranetLink() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id?: number; input: IntranetLinkInput }) =>
      id
        ? api.put<IntranetLink>(`/api/intranet/links/${id}`, input)
        : api.post<IntranetLink>('/api/intranet/links', input),
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useDeleteIntranetLink() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/intranet/links/${id}`),
    onSuccess: () => intranetInvalidation(client)
  })
}

// --- Arbejdsgrupper (beslutning 101), interne opgaver (102) og tallet i menuen (103) ---

import type {
  IntranetGroup,
  IntranetGroupInput,
  IntranetTask,
  IntranetTaskInput,
  IntranetUnseen
} from './types'

export const useIntranetGroups = (includeInactive = false, enabled = true) =>
  useQuery({
    queryKey: ['intranet', 'groups', includeInactive],
    queryFn: () =>
      api.get<IntranetGroup[]>(`/api/intranet/groups${includeInactive ? '?includeInactive=true' : ''}`),
    enabled
  })

export const useIntranetGroup = (id: number, enabled = true) =>
  useQuery({
    queryKey: ['intranet', 'groups', 'one', id],
    queryFn: () => api.get<IntranetGroup>(`/api/intranet/groups/${id}`),
    enabled: enabled && id > 0
  })

export function useSaveIntranetGroup() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id?: number; input: IntranetGroupInput }) =>
      id
        ? api.put<IntranetGroup>(`/api/intranet/groups/${id}`, input)
        : api.post<IntranetGroup>('/api/intranet/groups', input),
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useDeleteIntranetGroup() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/intranet/groups/${id}`),
    onSuccess: () => intranetInvalidation(client)
  })
}

export const useIntranetTasks = (
  options: { includeDone?: boolean; mine?: boolean; groupId?: number } = {},
  enabled = true
) =>
  useQuery({
    queryKey: ['intranet', 'tasks', options],
    queryFn: () => {
      const params = new URLSearchParams()
      if (options.includeDone) params.set('includeDone', 'true')
      if (options.mine) params.set('mine', 'true')
      if (options.groupId) params.set('groupId', String(options.groupId))

      const query = params.toString()
      return api.get<IntranetTask[]>(`/api/intranet/tasks${query ? `?${query}` : ''}`)
    },
    enabled
  })

export function useSaveIntranetTask() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id?: number; input: IntranetTaskInput }) =>
      id
        ? api.put<IntranetTask>(`/api/intranet/tasks/${id}`, input)
        : api.post<IntranetTask>('/api/intranet/tasks', input),
    onSuccess: () => intranetInvalidation(client)
  })
}

/** Saetter eller fjerner fluebenet paa en intern opgave. */
export function useSetIntranetTaskDone() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, done }: { id: number; done: boolean }) =>
      api.post<IntranetTask>(`/api/intranet/tasks/${id}/done?done=${done}`, {}),
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useDeleteIntranetTask() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/intranet/tasks/${id}`),
    onSuccess: () => intranetInvalidation(client)
  })
}

/**
 * Tallet ved "Intranet" i menuen (beslutning 103).
 *
 * Hentes ved hvert sideskift og hvert femte minut - realtidsbeskeden `intranet`
 * henter den ogsaa, saa et nyt opslag dukker op med det samme.
 */
// --- Kvitteringer (beslutning 114) ---

function receiptInvalidation(client: ReturnType<typeof useQueryClient>) {
  void client.invalidateQueries({ queryKey: ['receipts'] })
  void client.invalidateQueries({ queryKey: ['notifications'] })
}

/**
 * Er man paa kontoret, og hvor mange venter. Bruges ogsaa af striben og tallet i menuen (Simon 2026-10-01) -
 * derfor hentes det igen hvert 2. minut, ud over realtidsbeskeden ved en ny kvittering.
 */
export const useReceiptMeta = (enabled = true) =>
  useQuery({
    queryKey: ['receipts', 'meta'],
    queryFn: () => api.get<import('./types').ReceiptMeta>('/api/receipts/meta'),
    enabled,
    refetchInterval: 2 * 60 * 1000
  })

export type ReceiptView = 'mine' | 'pending' | 'notExported' | 'all'

/**
 * `mine` = egne, `pending` = dem der venter (kontoret), `notExported` = godkendte, som ingen har hentet eller
 * udskrevet (kontoret, beslutning 134), `all` = alles (kontoret). `search` og `month` ("yyyy-MM") filtrerer.
 */
export const useReceipts = (view: ReceiptView, search = '', month = '') =>
  useQuery({
    queryKey: ['receipts', view, search, month],
    queryFn: () => {
      const params = new URLSearchParams()
      if (view !== 'mine') params.set('all', 'true')
      if (view === 'pending') params.set('status', 'pending')
      if (view === 'notExported') params.set('notExported', 'true')
      if (search.trim()) params.set('q', search.trim())
      if (month) params.set('month', month)
      const query = params.toString()
      return api.get<import('./types').Receipt[]>(`/api/receipts${query ? `?${query}` : ''}`)
    },
    placeholderData: (previous) => previous
  })

export function useSubmitReceipt() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      orderNumber,
      paymentMethod,
      file
    }: {
      orderNumber: string
      paymentMethod: import('./types').ReceiptPaymentMethod
      file: Blob
    }) => uploadFile<import('./types').Receipt>('/api/receipts', file, 'kvittering.jpg', { orderNumber, paymentMethod }),
    onSuccess: () => receiptInvalidation(client)
  })
}

export function useApproveReceipt() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post<import('./types').Receipt>(`/api/receipts/${id}/approve`, {}),
    onSuccess: () => receiptInvalidation(client)
  })
}

export function useRejectReceipt() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      api.post<import('./types').Receipt>(`/api/receipts/${id}/reject`, { reason }),
    onSuccess: () => receiptInvalidation(client)
  })
}

/** Sender kvitteringerne videre på mail - én mail pr. kvittering, ordrenummeret som emne (beslutning 136). */
export function useMailReceipts() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (ids: number[]) => api.post<import('./types').ReceiptMailResult>('/api/receipts/send-mail', { ids }),
    onSuccess: () => receiptInvalidation(client)
  })
}

/** Noterer "Hent" eller "Udskriv" (beslutning 133). */
export function useRecordReceiptExport() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, kind }: { id: number; kind: 'download' | 'print' }) =>
      api.post<import('./types').Receipt>(`/api/receipts/${id}/exports`, { kind }),
    onSuccess: () => receiptInvalidation(client)
  })
}

/** Retter ordrenummeret (beslutning 132). */
export function useChangeReceiptOrderNumber() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, orderNumber }: { id: number; orderNumber: string }) =>
      api.put<import('./types').Receipt>(`/api/receipts/${id}/order-number`, { orderNumber }),
    onSuccess: () => receiptInvalidation(client)
  })
}

/** Skifter betalingsmåde (beslutning 148) - kun før godkendelsen. */
export function useChangeReceiptPaymentMethod() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, paymentMethod }: { id: number; paymentMethod: import('./types').ReceiptPaymentMethod }) =>
      api.put<import('./types').Receipt>(`/api/receipts/${id}/payment-method`, { paymentMethod }),
    onSuccess: () => receiptInvalidation(client)
  })
}

export function useDeleteReceipt() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/receipts/${id}`),
    onSuccess: () => receiptInvalidation(client)
  })
}

// --- Velkommen til nye (beslutning 118) ---

export const useWelcomeItems = () =>
  useQuery({
    queryKey: ['intranet', 'welcome', 'items'],
    queryFn: () => api.get<import('./types').WelcomeItem[]>('/api/intranet/welcome/items')
  })

export const useWelcomeStatus = (enabled: boolean) =>
  useQuery({
    queryKey: ['intranet', 'welcome', 'status'],
    queryFn: () => api.get<import('./types').WelcomeStatus[]>('/api/intranet/welcome/status'),
    enabled
  })

/** Forslag: medarbejder eller lærling ud fra hver persons roller. */
export const useWelcomeSuggestions = (enabled: boolean) =>
  useQuery({
    queryKey: ['intranet', 'welcome', 'suggestions'],
    queryFn: () =>
      api.get<{ userId: number; track: 'employee' | 'apprentice' }[]>('/api/intranet/welcome/suggestions'),
    enabled
  })

export function useWelcome() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      userId: number
      startDate: string
      text: string | null
      officeAssigneeUserId: number | null
      notify: boolean
      track: 'employee' | 'apprentice'
    }) => api.post<{ postId: number; officeTasks: number; newcomerTasks: number }>('/api/intranet/welcome', input),
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useSaveWelcomeItem() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      input
    }: {
      id?: number
      input: {
        title: string
        audience: string
        dueDaysAfterStart: number
        sortOrder: number
        isActive: boolean
        track: string
      }
    }) =>
      id
        ? api.put<import('./types').WelcomeItem>(`/api/intranet/welcome/items/${id}`, input)
        : api.post<import('./types').WelcomeItem>('/api/intranet/welcome/items', input),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['intranet', 'welcome', 'items'] })
  })
}

export function useDeleteWelcomeItem() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/intranet/welcome/items/${id}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['intranet', 'welcome', 'items'] })
  })
}

// --- Foedselsdage og jubilaeer (beslutning 117) ---

export const useCelebrations = (days: number, enabled = true) =>
  useQuery({
    queryKey: ['intranet', 'celebrations', days],
    queryFn: () => api.get<import('./types').Celebration[]>(`/api/intranet/celebrations?days=${days}`),
    enabled,
    staleTime: 30 * 60 * 1000
  })

export const useMyBirthday = () =>
  useQuery({
    queryKey: ['me', 'birthday'],
    queryFn: () => api.get<import('./types').MyBirthday>('/api/me/birthday')
  })

export function useSaveMyBirthday() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { month: number | null; day: number | null; show: boolean }) =>
      api.put<import('./types').MyBirthday>('/api/me/birthday', input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['me', 'birthday'] })
      void client.invalidateQueries({ queryKey: ['intranet', 'celebrations'] })
    }
  })
}

export const useHireDates = (enabled: boolean) =>
  useQuery({
    queryKey: ['intranet', 'hire-dates'],
    queryFn: () => api.get<import('./types').HireDate[]>('/api/intranet/hire-dates'),
    enabled
  })

export function useSaveHireDate() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, hireDate }: { userId: number; hireDate: string | null }) =>
      api.put<import('./types').HireDate>(`/api/intranet/hire-dates/${userId}`, { hireDate }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['intranet', 'hire-dates'] })
      void client.invalidateQueries({ queryKey: ['intranet', 'celebrations'] })
    }
  })
}

// --- Hurtige afstemninger (beslutning 116) ---

export const useIntranetPolls = (includeClosed: boolean) =>
  useQuery({
    queryKey: ['intranet', 'polls', { includeClosed }],
    queryFn: () =>
      api.get<import('./types').IntranetPoll[]>(`/api/intranet/polls${includeClosed ? '?includeClosed=true' : ''}`)
  })

export function useCreateIntranetPoll() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      question: string
      options: string[]
      allowMultiple: boolean
      isSecret: boolean
      closesAt: string | null
      groupId: number | null
    }) => api.post<import('./types').IntranetPoll>('/api/intranet/polls', input),
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useVoteIntranetPoll() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, optionIds }: { id: number; optionIds: number[] }) =>
      api.put<import('./types').IntranetPoll>(`/api/intranet/polls/${id}/vote`, { optionIds }),
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useCloseIntranetPoll() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post<import('./types').IntranetPoll>(`/api/intranet/polls/${id}/close`, {}),
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useDeleteIntranetPoll() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/intranet/polls/${id}`),
    onSuccess: () => intranetInvalidation(client)
  })
}

// --- Ideboksen (beslutning 115) ---

export const useIntranetIdeas = () =>
  useQuery({
    queryKey: ['intranet', 'ideas'],
    queryFn: () => api.get<import('./types').IntranetIdea[]>('/api/intranet/ideas')
  })

export function useSubmitIntranetIdea() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { title: string; description: string; isAnonymous: boolean }) =>
      api.post<import('./types').IntranetIdea>('/api/intranet/ideas', input),
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useAnswerIntranetIdea() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status, answer }: { id: number; status: string; answer: string }) =>
      api.put<import('./types').IntranetIdea>(`/api/intranet/ideas/${id}/answer`, { status, answer }),
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useDeleteIntranetIdea() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/intranet/ideas/${id}`),
    onSuccess: () => intranetInvalidation(client)
  })
}

// --- Naerved-haendelser (beslutning 113) ---

export const useIntranetIncidentMeta = () =>
  useQuery({
    queryKey: ['intranet', 'incidents', 'meta'],
    queryFn: () => api.get<import('./types').IntranetIncidentMeta>('/api/intranet/incidents/meta')
  })

export const useIntranetIncidents = (includeClosed: boolean) =>
  useQuery({
    queryKey: ['intranet', 'incidents', { includeClosed }],
    queryFn: () =>
      api.get<import('./types').IntranetIncident[]>(
        `/api/intranet/incidents${includeClosed ? '?includeClosed=true' : ''}`
      )
  })

/** Melder en haendelse - billederne sendes med i samme kald. */
export function useReportIntranetIncident() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      kind: import('./types').IntranetIncidentKind
      occurredAt: string | null
      location: string
      description: string
      isAnonymous: boolean
      photos: { blob: Blob; fileName: string }[]
    }) => {
      const form = new FormData()
      form.append('kind', input.kind)
      if (input.occurredAt) form.append('occurredAt', input.occurredAt)
      form.append('location', input.location)
      form.append('description', input.description)
      form.append('isAnonymous', String(input.isAnonymous))
      for (const photo of input.photos) {
        form.append('photos', photo.blob, photo.fileName)
      }
      return postForm<import('./types').IntranetIncident>('/api/intranet/incidents', form)
    },
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useFollowUpIntranetIncident() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status, followUp }: { id: number; status: string; followUp: string }) =>
      api.put<import('./types').IntranetIncident>(`/api/intranet/incidents/${id}/follow-up`, { status, followUp }),
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useDeleteIntranetIncident() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/intranet/incidents/${id}`),
    onSuccess: () => intranetInvalidation(client)
  })
}

export const useIntranetUnseen = (enabled = true) =>
  useQuery({
    queryKey: ['intranet', 'unseen'],
    queryFn: () => api.get<IntranetUnseen>('/api/intranet/unseen'),
    enabled,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 60 * 1000
  })

/** Saetter maerket "set nu". Kaldes, naar intranettet aabnes. */
export function useMarkIntranetSeen() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<void>('/api/intranet/seen', {}),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['intranet', 'unseen'] })
  })
}

// --- Personalehaandbog og dokumenter (beslutning 104) ---

import type { IntranetDocument, IntranetDocumentInput } from './types'

export const useIntranetDocuments = (includeInactive = false, enabled = true) =>
  useQuery({
    queryKey: ['intranet', 'documents', includeInactive],
    queryFn: () =>
      api.get<IntranetDocument[]>(
        `/api/intranet/documents${includeInactive ? '?includeInactive=true' : ''}`
      ),
    enabled
  })

export function useSaveIntranetDocument() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id?: number; input: IntranetDocumentInput }) =>
      id
        ? api.put<IntranetDocument>(`/api/intranet/documents/${id}`, input)
        : api.post<IntranetDocument>('/api/intranet/documents', input),
    onSuccess: () => intranetInvalidation(client)
  })
}

/** Laegger filen op i SharePoint. Dokumentet skal findes foerst - filen skal haenge paa noget. */
export function useUploadIntranetDocumentFile() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, file }: { id: number; file: File }) =>
      uploadFile<IntranetDocument>(`/api/intranet/documents/${id}/file`, file, file.name),
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useDeleteIntranetDocument() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/intranet/documents/${id}`),
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useMarkIntranetDocumentRead() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post<void>(`/api/intranet/documents/${id}/read`, {}),
    onSuccess: () => intranetInvalidation(client)
  })
}

export const useIntranetDocumentReads = (id: number, enabled = true) =>
  useQuery({
    queryKey: ['intranet', 'documents', id, 'reads'],
    queryFn: () => api.get<import('./types').IntranetPostReads>(`/api/intranet/documents/${id}/reads`),
    enabled: enabled && id > 0
  })

// ---------- Enhedstyper og modeller (beslutning 105) ----------

/** Den faste liste over typer med deres modeller. */
export const useDeviceTypes = (includeInactive = false) =>
  useQuery({
    queryKey: ['device-types', includeInactive],
    queryFn: () =>
      api.get<import('./types').DeviceTypeOption[]>(`/api/device-types${includeInactive ? '?includeInactive=true' : ''}`),
    staleTime: 5 * 60 * 1000
  })

function deviceTypeInvalidation(client: ReturnType<typeof useQueryClient>) {
  void client.invalidateQueries({ queryKey: ['device-types'] })
  // En omdøbning retter teksten paa enheder og checklister.
  void client.invalidateQueries({ queryKey: ['devices'] })
  void client.invalidateQueries({ queryKey: ['service-checklists'] })
}

/** Tilføjer en type (Projektleder og op). Findes den, gives den bare tilbage. */
export function useCreateDeviceType() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (name: string) => api.post<import('./types').DeviceTypeOption>('/api/device-types', { name }),
    onSuccess: () => deviceTypeInvalidation(client)
  })
}

/** Tilføjer en model under en type (Projektleder og op). */
export function useCreateDeviceModel() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ typeId, name, manufacturer }: { typeId: number; name: string; manufacturer?: string | null }) =>
      api.post<import('./types').DeviceModelOption>(`/api/device-types/${typeId}/models`, { name, manufacturer }),
    onSuccess: () => deviceTypeInvalidation(client)
  })
}

/** Retter en type - navn, rækkefølge, til/fra (Administrator). */
export function useUpdateDeviceType() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, name, sortOrder, isActive }: { id: number; name: string; sortOrder?: number; isActive?: boolean }) =>
      api.put<import('./types').DeviceTypeOption>(`/api/device-types/${id}`, { name, sortOrder, isActive }),
    onSuccess: () => deviceTypeInvalidation(client)
  })
}

export function useDeleteDeviceType() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/device-types/${id}`),
    onSuccess: () => deviceTypeInvalidation(client)
  })
}

/** Retter en model (Administrator). */
export function useUpdateDeviceModel() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, name, manufacturer, isActive }: { id: number; name: string; manufacturer?: string | null; isActive?: boolean }) =>
      api.put<import('./types').DeviceModelOption>(`/api/device-types/models/${id}`, { name, manufacturer, isActive }),
    onSuccess: () => deviceTypeInvalidation(client)
  })
}

export function useDeleteDeviceModel() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/device-types/models/${id}`),
    onSuccess: () => deviceTypeInvalidation(client)
  })
}

// --- Vagtplan og tilkaldevagt (beslutning 120) ---

/** Hvem har vagten nu, og hvem har den næste gang - til forsiden. */
export const useOnCallNow = (enabled = true) =>
  useQuery({
    queryKey: ['intranet', 'oncall', 'now'],
    queryFn: () => api.get<import('./types').OnCallNow>('/api/intranet/oncall/now'),
    enabled,
    staleTime: 5 * 60 * 1000
  })

export const useOnCallPlan = (weeks = 26) =>
  useQuery({
    queryKey: ['intranet', 'oncall', 'plan', weeks],
    queryFn: () => api.get<import('./types').OnCallPlan>(`/api/intranet/oncall?weeks=${weeks}`)
  })

/** Rækkefølgen, vagten går på omgang i. Kontoret. */
export function useSetOnCallRotation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (userIds: number[]) =>
      api.put<import('./types').UserRef[]>('/api/intranet/oncall/rotation', { userIds }),
    onSuccess: () => intranetInvalidation(client)
  })
}

/** Lægger rækkefølgen på de uger frem, der ikke har en vagt. Svarer med antallet af nye uger. */
export function useFillOnCall() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (weeks: number) => api.post<number>('/api/intranet/oncall/fill', { weeks }),
    onSuccess: () => intranetInvalidation(client)
  })
}

/** Sætter eller fjerner vagten på én uge. Kontoret. */
export function useAssignOnCall() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { weekStart: string; userId: number | null; note?: string | null }) =>
      api.put<void>(`/api/intranet/oncall/weeks/${input.weekStart}`, { userId: input.userId, note: input.note ?? null }),
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useRequestOnCallSwap() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { weekStart: string; targetUserId: number; otherWeekStart?: string | null; message?: string }) =>
      api.post<void>('/api/intranet/oncall/swaps', input),
    onSuccess: () => intranetInvalidation(client)
  })
}

/** Ja, nej eller træk tilbage. */
export function useAnswerOnCallSwap() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: number; action: 'accept' | 'decline' | 'cancel' }) =>
      api.post<void>(`/api/intranet/oncall/swaps/${input.id}/${input.action}`),
    onSuccess: () => intranetInvalidation(client)
  })
}

// --- Ros, køb/salg og ugens faglige tip (beslutning 121) ---

/** Én måned på fanen "Ros" (yyyy-MM). */
export const useIntranetKudos = (month: string) =>
  useQuery({
    queryKey: ['intranet', 'kudos', month],
    queryFn: () => api.get<import('./types').IntranetKudosMonth>(`/api/intranet/kudos?month=${month}`)
  })

export function useCreateIntranetKudos() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { recipientIds: number[]; message: string }) =>
      api.post<import('./types').IntranetKudos>('/api/intranet/kudos', input),
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useDeleteIntranetKudos() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/intranet/kudos/${id}`),
    onSuccess: () => intranetInvalidation(client)
  })
}

export const useIntranetMarket = () =>
  useQuery({
    queryKey: ['intranet', 'market'],
    queryFn: () => api.get<import('./types').IntranetMarketItem[]>('/api/intranet/market')
  })

export function useCreateIntranetMarketItem() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      kind: string
      title: string
      description: string
      price: string
      photos: { blob: Blob; fileName: string }[]
    }) => {
      const form = new FormData()
      form.append('kind', input.kind)
      form.append('title', input.title)
      form.append('description', input.description)
      form.append('price', input.price)
      for (const photo of input.photos) {
        form.append('photos', photo.blob, photo.fileName)
      }
      return postForm<import('./types').IntranetMarketItem>('/api/intranet/market', form)
    },
    onSuccess: () => intranetInvalidation(client)
  })
}

/** "Afslut", "Forlæng" eller slet. */
export function useChangeIntranetMarketItem() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: number; action: 'close' | 'renew' | 'delete' }) =>
      input.action === 'delete'
        ? api.delete<void>(`/api/intranet/market/${input.id}`)
        : api.post<void>(`/api/intranet/market/${input.id}/${input.action}`),
    onSuccess: () => intranetInvalidation(client)
  })
}

export const useIntranetTips = () =>
  useQuery({
    queryKey: ['intranet', 'tips'],
    queryFn: () => api.get<import('./types').IntranetTip[]>('/api/intranet/tips')
  })

/** Skriver eller retter et tip (kontoret). Med `id` rettes det. */
export function useSaveIntranetTip() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      id?: number
      title: string
      body: string
      photo?: { blob: Blob; fileName: string } | null
      removePhoto?: boolean
    }) => {
      const form = new FormData()
      form.append('title', input.title)
      form.append('body', input.body)
      form.append('removePhoto', String(input.removePhoto ?? false))
      if (input.photo) form.append('photo', input.photo.blob, input.photo.fileName)
      return input.id
        ? postForm<import('./types').IntranetTip>(`/api/intranet/tips/${input.id}`, form, 'PUT')
        : postForm<import('./types').IntranetTip>('/api/intranet/tips', form)
    },
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useDeleteIntranetTip() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/intranet/tips/${id}`),
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useMarkIntranetTipRead() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post<void>(`/api/intranet/tips/${id}/read`),
    onSuccess: () => intranetInvalidation(client)
  })
}

// --- Firmabiler (beslutning 123) ---

function vehicleInvalidation(client: ReturnType<typeof useQueryClient>) {
  void client.invalidateQueries({ queryKey: ['vehicles'] })
  void client.invalidateQueries({ queryKey: ['activity'] })
}

export const useVehicles = () =>
  useQuery({
    queryKey: ['vehicles'],
    queryFn: () => api.get<import('./types').Vehicle[]>('/api/vehicles')
  })

export const useVehicle = (id: number) =>
  useQuery({
    queryKey: ['vehicles', id],
    queryFn: () => api.get<import('./types').VehicleDetail>(`/api/vehicles/${id}`),
    enabled: id > 0
  })

/** Opretter (uden id) eller retter en bil. Kontoret. */
export function useSaveVehicle() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id?: number; input: import('./types').VehicleSaveInput }) =>
      id
        ? api.put<import('./types').VehicleDetail>(`/api/vehicles/${id}`, input)
        : api.post<import('./types').VehicleDetail>('/api/vehicles', input),
    onSuccess: () => vehicleInvalidation(client)
  })
}

export function useDeleteVehicle() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/vehicles/${id}`),
    onSuccess: () => vehicleInvalidation(client)
  })
}

export function useSetVehicleKm() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, km }: { id: number; km: number }) => api.post<void>(`/api/vehicles/${id}/km`, { km }),
    onSuccess: () => vehicleInvalidation(client)
  })
}

export function useSetVehicleTyres() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, season }: { id: number; season: 'summer' | 'winter' | 'all_season' }) =>
      api.post<void>(`/api/vehicles/${id}/tyres`, { season }),
    onSuccess: () => vehicleInvalidation(client)
  })
}

/** Billedet af bilen (beslutning 135). Kontoret. */
export function useSetVehiclePhoto() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, photo }: { id: number; photo: { blob: Blob; fileName: string } }) => {
      const form = new FormData()
      form.append('photo', photo.blob, photo.fileName)
      return postForm<import('./types').VehicleDetail>(`/api/vehicles/${id}/photo`, form, 'PUT')
    },
    onSuccess: () => vehicleInvalidation(client)
  })
}

export function useDeleteVehiclePhoto() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/vehicles/${id}/photo`),
    onSuccess: () => vehicleInvalidation(client)
  })
}

export function useReportVehicleDamage() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      vehicleId: number
      description: string
      location: string
      occurredOn?: string | null
      photos: { blob: Blob; fileName: string }[]
      /** Tilkaldt (beslutning 135). */
      roadsideAssistanceCalled: boolean
      policeCalled: boolean
      ambulanceCalled: boolean
      hcAutoCalled: boolean
      /** Modparten (beslutning 135). */
      counterparty?: {
        plate: string
        name: string
        phone: string
        insurance: string
        photos: { blob: Blob; fileName: string }[]
      } | null
    }) => {
      const form = new FormData()
      form.append('description', input.description)
      form.append('location', input.location)
      if (input.occurredOn) form.append('occurredOn', input.occurredOn)
      for (const photo of input.photos) form.append('photos', photo.blob, photo.fileName)
      form.append('roadsideAssistanceCalled', String(input.roadsideAssistanceCalled))
      form.append('policeCalled', String(input.policeCalled))
      form.append('ambulanceCalled', String(input.ambulanceCalled))
      form.append('hcAutoCalled', String(input.hcAutoCalled))
      if (input.counterparty) {
        form.append('hasCounterparty', 'true')
        form.append('counterpartyPlate', input.counterparty.plate)
        form.append('counterpartyName', input.counterparty.name)
        form.append('counterpartyPhone', input.counterparty.phone)
        form.append('counterpartyInsurance', input.counterparty.insurance)
        for (const photo of input.counterparty.photos) form.append('counterpartyPhotos', photo.blob, photo.fileName)
      }
      return postForm<import('./types').VehicleDamage>(`/api/vehicles/${input.vehicleId}/damages`, form)
    },
    onSuccess: () => vehicleInvalidation(client)
  })
}

export function useFollowUpVehicleDamage() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { vehicleId: number; damageId: number; status: string; followUp: string }) =>
      api.put<void>(`/api/vehicles/${input.vehicleId}/damages/${input.damageId}`, {
        status: input.status,
        followUp: input.followUp
      }),
    onSuccess: () => vehicleInvalidation(client)
  })
}

export function useDeleteVehicleDamage() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ vehicleId, damageId }: { vehicleId: number; damageId: number }) =>
      api.delete<void>(`/api/vehicles/${vehicleId}/damages/${damageId}`),
    onSuccess: () => vehicleInvalidation(client)
  })
}

// --- Fælles måltider (beslutning 124) ---

export const useIntranetMeals = () =>
  useQuery({
    queryKey: ['intranet', 'meals'],
    queryFn: () => api.get<import('./types').IntranetMeal[]>('/api/intranet/meals')
  })

/** Opretter (uden id) eller retter et måltid. Kontoret. */
export function useSaveIntranetMeal() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      id?: number
      title: string
      description: string
      servedAt: string
      closesAt: string
      choreText: string
    }) =>
      input.id
        ? api.put<import('./types').IntranetMeal>(`/api/intranet/meals/${input.id}`, input)
        : api.post<import('./types').IntranetMeal>('/api/intranet/meals', input),
    onSuccess: () => intranetInvalidation(client)
  })
}

/** Tilmeld, meld fra, tag eller giv tjansen fra dig, eller aflys (kontoret). */
export function useChangeIntranetMeal() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: number; action: 'signup' | 'signoff' | 'take' | 'release' | 'cancel'; needs?: string }) => {
      const base = `/api/intranet/meals/${input.id}`
      switch (input.action) {
        case 'signup':
          return api.post<void>(`${base}/signup`, { needs: input.needs ?? null })
        case 'signoff':
          return api.delete<void>(`${base}/signup`)
        case 'take':
          return api.post<void>(`${base}/chore`)
        case 'release':
          return api.delete<void>(`${base}/chore`)
        default:
          return api.post<void>(`${base}/cancel`)
      }
    },
    onSuccess: () => intranetInvalidation(client)
  })
}

// --- APV og kemikalier (beslutning 125) ---

export const useApv = () =>
  useQuery({
    queryKey: ['intranet', 'apv'],
    queryFn: () => api.get<import('./types').ApvOverview>('/api/intranet/apv')
  })

export function useStartApv() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { title: string; closesAt: string; questions: { area: string; text: string }[] }) =>
      api.post<import('./types').ApvRound>('/api/intranet/apv/rounds', input),
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useAnswerApv() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { roundId: number; answers: { questionId: number; value: string; comment?: string }[] }) =>
      api.post<void>(`/api/intranet/apv/rounds/${input.roundId}/answers`, { answers: input.answers }),
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useAddApvAction() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      roundId: number
      questionId?: number | null
      title: string
      description?: string
      assigneeUserId: number
      dueDate: string
    }) => api.post<import('./types').ApvRound>(`/api/intranet/apv/rounds/${input.roundId}/actions`, input),
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useCompleteApv() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (roundId: number) => api.post<void>(`/api/intranet/apv/rounds/${roundId}/complete`),
    onSuccess: () => intranetInvalidation(client)
  })
}

export const useChemicals = (includeInactive = false) =>
  useQuery({
    queryKey: ['intranet', 'chemicals', includeInactive],
    queryFn: () =>
      api.get<import('./types').Chemical[]>(`/api/intranet/chemicals${includeInactive ? '?includeInactive=true' : ''}`)
  })

export function useSaveChemical() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id?: number; input: import('./types').ChemicalSaveInput }) =>
      id
        ? api.put<import('./types').Chemical>(`/api/intranet/chemicals/${id}`, input)
        : api.post<import('./types').Chemical>('/api/intranet/chemicals', input),
    onSuccess: () => intranetInvalidation(client)
  })
}

export function useDeleteChemical() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/intranet/chemicals/${id}`),
    onSuccess: () => intranetInvalidation(client)
  })
}

/** Lægger sikkerhedsdatablad (sds) eller brugsanvisning (apb) op. Kontoret. */
export function useUploadChemicalFile() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, kind, file }: { id: number; kind: 'sds' | 'apb'; file: File }) =>
      uploadFile<import('./types').Chemical>(`/api/intranet/chemicals/${id}/files/${kind}`, file, file.name),
    onSuccess: () => intranetInvalidation(client)
  })
}


// ---------- Varer til bilen (beslutning 127) ----------

export const useVanOrderContext = () =>
  useQuery({
    queryKey: ['van-orders', 'context'],
    queryFn: () => api.get<import('./types').VanOrderContext>('/api/stock/van-orders/context'),
    staleTime: 60_000
  })

export const useVanOrders = (open = false) =>
  useQuery({
    queryKey: ['van-orders', 'list', open],
    queryFn: () => api.get<import('./types').VanOrder[]>(`/api/stock/van-orders${open ? '?open=true' : ''}`)
  })

export const useVanOrder = (id: number) =>
  useQuery({
    queryKey: ['van-orders', 'detail', id],
    queryFn: () => api.get<import('./types').VanOrder>(`/api/stock/van-orders/${id}`),
    enabled: id > 0
  })

/** "Fyld op": varerne under bilens minimum. Hentes først, når der trykkes. */
export const useVanOrderSuggestion = () =>
  useMutation({
    mutationFn: (vanLocationId: number) =>
      api.get<import('./types').VanOrderSuggestion[]>(
        `/api/stock/van-orders/suggestion?vanLocationId=${vanLocationId}`
      )
  })

function useVanOrderMutation<TInput>(request: (input: TInput) => Promise<import('./types').VanOrder>) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: request,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['van-orders'] })
      // "Hentet" flytter varer - lagerets tal skal med.
      void client.invalidateQueries({ queryKey: ['stock'] })
    }
  })
}

export const useCreateVanOrder = () =>
  useVanOrderMutation((input: import('./types').VanOrderInput) =>
    api.post<import('./types').VanOrder>('/api/stock/van-orders', input)
  )

export const usePackVanOrder = () =>
  useVanOrderMutation(({ id, input }: { id: number; input: import('./types').VanOrderPackInput }) =>
    api.post<import('./types').VanOrder>(`/api/stock/van-orders/${id}/pack`, input)
  )

export const usePickUpVanOrder = () =>
  useVanOrderMutation((id: number) => api.post<import('./types').VanOrder>(`/api/stock/van-orders/${id}/pickup`, {}))

export const useCancelVanOrder = () =>
  useVanOrderMutation((id: number) => api.post<import('./types').VanOrder>(`/api/stock/van-orders/${id}/cancel`, {}))

export const useReorderVanOrderRest = () =>
  useVanOrderMutation((id: number) =>
    api.post<import('./types').VanOrder>(`/api/stock/van-orders/${id}/reorder-rest`, {})
  )

// ---------- Personalemøder (beslutning 128) ----------

export const useIntranetMeetings = () =>
  useQuery({
    queryKey: ['intranet', 'meetings'],
    queryFn: () => api.get<import('./types').IntranetMeetingListItem[]>('/api/intranet/meetings')
  })

export const useIntranetMeeting = (id: number) =>
  useQuery({
    queryKey: ['intranet', 'meetings', id],
    queryFn: () => api.get<import('./types').IntranetMeeting>(`/api/intranet/meetings/${id}`),
    enabled: id > 0
  })

/** Alle ændringer af et møde svarer med mødet - listen og tallet ved Intranet hentes igen. */
export function useIntranetMeetingAction() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ path, method = 'post', body }: { path: string; method?: 'post' | 'put' | 'delete'; body?: unknown }) =>
      method === 'put'
        ? api.put<import('./types').IntranetMeeting>(`/api/intranet/meetings${path}`, body ?? {})
        : method === 'delete'
          ? api.delete<import('./types').IntranetMeeting>(`/api/intranet/meetings${path}`)
          : api.post<import('./types').IntranetMeeting>(`/api/intranet/meetings${path}`, body ?? {}),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['intranet'] })
    }
  })
}

// ---------- MUS-samtaler (beslutning 129) ----------

export const useMyMus = () =>
  useQuery({
    queryKey: ['intranet', 'mus', 'mine'],
    queryFn: () => api.get<import('./types').MusSummary[]>('/api/intranet/mus')
  })

/** Kontorets oversigt - hvem, hvornår og status. Aldrig indholdet. */
export const useMusOverview = (enabled: boolean) =>
  useQuery({
    queryKey: ['intranet', 'mus', 'overview'],
    queryFn: () => api.get<import('./types').MusOverviewRow[]>('/api/intranet/mus/overview'),
    enabled
  })

export const useMusConversation = (id: number) =>
  useQuery({
    queryKey: ['intranet', 'mus', id],
    queryFn: () => api.get<import('./types').MusConversation>(`/api/intranet/mus/${id}`),
    enabled: id > 0
  })

/** Alle ændringer af MUS - listen, oversigten og tallet ved Intranet hentes igen. */
export function useMusAction() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ path, method = 'post', body }: { path: string; method?: 'post' | 'put'; body?: unknown }) =>
      method === 'put'
        ? api.put<unknown>(`/api/intranet/mus${path}`, body ?? {})
        : api.post<unknown>(`/api/intranet/mus${path}`, body ?? {}),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['intranet'] })
    }
  })
}

export const useMusQuestions = (enabled: boolean) =>
  useQuery({
    queryKey: ['intranet', 'mus', 'questions'],
    queryFn: () => api.get<import('./types').MusQuestion[]>('/api/intranet/mus/questions'),
    enabled
  })

// ---------- Lærlingeplan (beslutning 130) ----------

export const useApprenticeships = (enabled = true) =>
  useQuery({
    queryKey: ['intranet', 'apprentices'],
    queryFn: () => api.get<import('./types').ApprenticeshipListItem[]>('/api/intranet/apprentices'),
    enabled
  })

export const useApprenticeship = (id: number) =>
  useQuery({
    queryKey: ['intranet', 'apprentices', id],
    queryFn: () => api.get<import('./types').Apprenticeship>(`/api/intranet/apprentices/${id}`),
    enabled: id > 0
  })

/** Alle ændringer af en lærlingeplan. Svaret er planen - listen hentes igen. */
export function useApprenticeAction() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ path, method = 'post', body, form }: {
      path: string
      method?: 'post' | 'put' | 'delete'
      body?: unknown
      form?: FormData
    }) =>
      form
        ? postForm<import('./types').Apprenticeship>(`/api/intranet/apprentices${path}`, form)
        : method === 'put'
          ? api.put<import('./types').Apprenticeship>(`/api/intranet/apprentices${path}`, body ?? {})
          : method === 'delete'
            ? api.delete<import('./types').Apprenticeship>(`/api/intranet/apprentices${path}`)
            : api.post<import('./types').Apprenticeship>(`/api/intranet/apprentices${path}`, body ?? {}),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['intranet', 'apprentices'] })
    }
  })
}

export const useApprenticeGoalTemplates = (enabled: boolean) =>
  useQuery({
    queryKey: ['intranet', 'apprentices', 'goal-templates'],
    queryFn: () => api.get<import('./types').ApprenticeGoalTemplate[]>('/api/intranet/apprentices/goal-templates'),
    enabled
  })

// ---------- Egenkontrol (beslutning 131) ----------

export const useProjectSelfChecks = (projectId: number, enabled = true) =>
  useQuery({
    queryKey: ['self-checks', 'project', projectId],
    queryFn: () => api.get<import('./types').ProjectSelfChecks>(`/api/projects/${projectId}/self-checks`),
    enabled: enabled && projectId > 0,
    retry: false
  })

export const useSelfCheck = (id: number) =>
  useQuery({
    queryKey: ['self-checks', id],
    queryFn: () => api.get<import('./types').SelfCheck>(`/api/self-checks/${id}`),
    enabled: id > 0
  })

/** Alle ændringer af en egenkontrol - svaret er egenkontrollen. Sagens liste og filer hentes igen. */
export function useSelfCheckAction() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ path, method = 'post', body }: { path: string; method?: 'post' | 'put' | 'delete'; body?: unknown }) =>
      method === 'put'
        ? api.put<import('./types').SelfCheck>(path, body ?? {})
        : method === 'delete'
          ? api.delete<import('./types').SelfCheck>(path)
          : api.post<import('./types').SelfCheck>(path, body ?? {}),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['self-checks'] })
      void client.invalidateQueries({ queryKey: ['projects'] })
    }
  })
}

export const useSelfCheckTemplate = (enabled: boolean) =>
  useQuery({
    queryKey: ['self-checks', 'template'],
    queryFn: () => api.get<import('./types').SelfCheckTemplateItem[]>('/api/self-checks/template-items'),
    enabled
  })

// ---------- Diagrammer på sagen (beslutning 150) - kun Projektleder og opefter ----------

import type { Diagram, DiagramContent, DiagramSummary } from './types'

export const useDiagrams = (projectId?: number) =>
  useQuery({
    queryKey: ['diagrams', projectId ?? 'all'],
    queryFn: () => api.get<DiagramSummary[]>(`/api/diagrams${projectId ? `?projectId=${projectId}` : ''}`)
  })

export const useDiagram = (id: number) =>
  useQuery({
    queryKey: ['diagrams', 'one', id],
    queryFn: () => api.get<Diagram>(`/api/diagrams/${id}`),
    enabled: Number.isFinite(id) && id > 0
  })

export function useCreateDiagram() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: { projectId: number; title: string }) => api.post<Diagram>('/api/diagrams', body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['diagrams'] })
  })
}

export function useSaveDiagram(id: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: { title: string; content: DiagramContent }) => api.put<Diagram>(`/api/diagrams/${id}`, body),
    onSuccess: (saved) => {
      client.setQueryData(['diagrams', 'one', id], saved)
      void client.invalidateQueries({ queryKey: ['diagrams', 'all'] })
      void client.invalidateQueries({ queryKey: ['diagrams', saved.summary.projectId] })
    }
  })
}

/** Tegner PDF'en på serveren og lægger den i sagens Dokumenter-mappe. */
export function useSaveDiagramPdf(id: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<Diagram>(`/api/diagrams/${id}/pdf`),
    onSuccess: (saved) => {
      client.setQueryData(['diagrams', 'one', id], saved)
      void client.invalidateQueries({ queryKey: ['diagrams'] })
      void client.invalidateQueries({ queryKey: ['projects', saved.summary.projectId, 'files'] })
    }
  })
}

export function useDeleteDiagram() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/diagrams/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['diagrams'] })
  })
}
